// Thin WebGL2 renderer for the static world (lightmapped BSP) plus dynamic
// entities. Kept allocation-light for the 360fps target: buffers are created
// once per map and reused.

import { computeAlpha, computeRgb, computeUv, waveFuncId } from "./shader";
import type { BlendFactor, BlendMode, ColorGen, CullMode, Deform, FogParms, TcMod } from "./shader";
import type { Md3Model } from "./md3";
import type { ParticleSystem } from "./particles";
import type { PlayerTrailSystem } from "./player_trail";
import { Bloom } from "./bloom";

/** Per-stage effects computed per frame by the renderer. */
export interface StageEffects {
  tcMods: TcMod[];
  rgb?: ColorGen;
  alpha?: ColorGen;
  /** Q3 `alphaFunc`: discard fragments by alpha (grates, foliage, banners). */
  alphaFunc?: "gt0" | "lt128" | "ge128";
  /** Q3 `depthFunc equal`: only draw where an earlier pass wrote depth. */
  depthFuncEqual?: boolean;
  tcGen?: "environment" | "lightmap" | "vector" | "ivector";
  /** `tcGen vector`: the two 3-vectors the world position is dotted with. */
  tcGenS?: [number, number, number];
  tcGenT?: [number, number, number];
  deform?: Deform;
}

/**
 * Material state for a shader's base pass plus its additive/blended overlay
 * passes. Shared by every index range drawn with that shader (used directly by
 * whole-shader `DrawChunk`s and by PVS-visible runs).
 */
export interface DrawMaterial {
  /** Base texture ids (animmap frames), or [] for untextured. */
  tex: number[];
  animFreq: number;
  blend: BlendMode;
  lit: boolean;
  /** Face culling for this material (`cull` directive; default front = keep visible). */
  cull: CullMode;
  /** Apply a polygon offset (decal/overlay surfaces). */
  polygonOffset?: boolean;
  effects: StageEffects;
  fog?: FogParms;
  /** Q3 fog volume: render as coloured fog, not as the shader's texture. */
  fogVolume?: boolean;
  overlays: Array<{ tex: number; blend: BlendMode; effects: StageEffects }>;
}

/** `WEBGL_multi_draw` (Chrome/ANGLE): several index ranges in one call. */
interface MultiDrawExt {
  multiDrawElementsWEBGL(
    mode: number,
    countsList: Int32Array,
    countsOffset: number,
    type: number,
    offsetsList: Int32Array,
    offsetsOffset: number,
    drawcount: number,
  ): void;
}

/** A draw chunk: a material plus one contiguous index range. */
export interface DrawChunk extends DrawMaterial {
  first: number;
  count: number;
}

/** A draw chunk belonging to a moving brush entity (indexed by `model`). */
export interface MoverChunk extends DrawChunk {
  model: number;
}

/** A portal-surface chunk plus its world-space plane (point + normal). */
export interface PortalChunk extends DrawChunk {
  plane: { n: [number, number, number]; p: [number, number, number] };
}

interface PortalFbo {
  fb: WebGLFramebuffer;
  color: WebGLTexture;
  depth: WebGLRenderbuffer;
  w: number;
  h: number;
}

/** One drawable surface of a runtime MD3 model, uploaded to the GPU. */
interface GpuModelSurface {
  posVbo: WebGLBuffer;
  stVbo: WebGLBuffer;
  ibo: WebGLBuffer;
  indexCount: number;
  numVerts: number;
  numFrames: number;
  tex: WebGLTexture | null;
  /** Debug/verification: uploaded position count + per-frame sums. */
  positionsLen: number;
  frameSums: number[];
}

interface GpuModel {
  surfaces: GpuModelSurface[];
}

/** A per-frame instance of a loaded model (matrix + lightgrid tint + frame). */
export interface ModelInstance {
  model: number;
  matrix: Float32Array;
  color: [number, number, number];
  alpha: number;
  frame0: number;
  frame1: number;
  lerp: number;
  /** Additive glow instance (neon outline shells, etc.). */
  glow?: boolean;
  /** Face-culling override; outline hulls cull front faces. */
  cull?: "front" | "back" | "none";
}

const IDENTITY = new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);

/**
 * `angles` (`[pitch, yaw, roll]` radians) → right-handed rotation matrix, using
 * the same convention as Rust `trace::angles_to_axis` (so zero angles is the
 * identity and yaw matches the camera). Rows are `anglesToAxis(angles)[0..2]`.
 */
export function anglesToAxis(angles: [number, number, number]): number[][] {
  const [p, y, r] = angles;
  const sp = Math.sin(p), cp = Math.cos(p);
  const sy = Math.sin(y), cy = Math.cos(y);
  const sr = Math.sin(r), cr = Math.cos(r);
  return [
    [cp * cy, cp * sy, -sp],
    [sr * sp * cy - cr * sy, sr * sp * sy + cr * cy, sr * cp],
    [cr * sp * cy + sr * sy, cr * sp * sy - sr * cy, cr * cp],
  ];
}

/** Model matrix (column-major) for an origin + `[pitch,yaw,roll]` + scale. */
export function modelMatrix(
  origin: [number, number, number],
  angles: [number, number, number],
  scale = 1,
): Float32Array {
  const a = anglesToAxis(angles);
  const m = new Float32Array(16);
  m[0] = a[0][0] * scale; m[1] = a[0][1] * scale; m[2] = a[0][2] * scale; m[3] = 0;
  m[4] = a[1][0] * scale; m[5] = a[1][1] * scale; m[6] = a[1][2] * scale; m[7] = 0;
  m[8] = a[2][0] * scale; m[9] = a[2][1] * scale; m[10] = a[2][2] * scale; m[11] = 0;
  m[12] = origin[0]; m[13] = origin[1]; m[14] = origin[2]; m[15] = 1;
  return m;
}

/** Texture filtering quality (maps to min/mag filters + mipmaps + anisotropy). */
export type TextureMode = "nearest" | "bilinear" | "trilinear" | "anisotropic";
export const TEXTURE_MODES: TextureMode[] = ["nearest", "bilinear", "trilinear", "anisotropic"];

export class Renderer {
  gl: WebGL2RenderingContext;
  private program: WebGLProgram;
  private vao: WebGLVertexArrayObject;
  private vbo: WebGLBuffer;
  private ibo: WebGLBuffer;
  private indexCount = 0;
  // Wireframe: a second VAO per mesh sharing the vertex buffer but using a
  // line-index buffer (WebGL2 has no polygon mode). Line indices are exactly
  // 2x the triangle indices in the same order, so chunk ranges scale by 2.
  private wireIbo: WebGLBuffer;
  private wireVao: WebGLVertexArrayObject;
  private wireIndexCount = 0;
  private wireframe = false;
  // Moving brush entities use a second vertex/index buffer so each mover can be
  // drawn with its own model matrix.
  private moverVao: WebGLVertexArrayObject | null = null;
  private moverVbo: WebGLBuffer | null = null;
  private moverIbo: WebGLBuffer | null = null;
  private moverWireIbo: WebGLBuffer | null = null;
  private moverWireVao: WebGLVertexArrayObject | null = null;
  private moverIndexCount = 0;
  private moverChunks: MoverChunk[] = [];
  // Portal surfaces: each gets its own render-to-texture framebuffer.
  private portalChunks: PortalChunk[] = [];
  private portalFbos = new Map<PortalChunk, PortalFbo>();
  private canvasW = 1;
  private canvasH = 1;
  private uProjView: WebGLUniformLocation;
  private uModel: WebGLUniformLocation;
  private uHasTexture: WebGLUniformLocation;
  private uLit: WebGLUniformLocation;
  private uWire: WebGLUniformLocation;
  // tcMod affine + rgbGen/alphaGen + fog/portal uniforms (base program).
  private uUvX: WebGLUniformLocation;
  private uUvY: WebGLUniformLocation;
  private uUvOff: WebGLUniformLocation;
  private uRgb: WebGLUniformLocation;
  private uRgbVertex: WebGLUniformLocation;
  private uAlpha: WebGLUniformLocation;
  private uAlphaFunc: WebGLUniformLocation;
  private uEye: WebGLUniformLocation;
  private uFogColor: WebGLUniformLocation;
  private uFogDist: WebGLUniformLocation;
  private uFogOn: WebGLUniformLocation;
  private uFogSurface: WebGLUniformLocation;
  /** Fog of the volume containing the camera (applied to every material). */
  private sceneFog: FogParms | null = null;
  /** Last frame's CPU submission time split, ms (debug/perf). */
  private lastDrawTimes: Record<string, number> = {};
  private lastWorldRuns = 0;
  private lastWorldGroups = 0;
  /** Portal reflection refresh rate (Hz); the FBO is reused between updates. */
  private portalHz = 30;
  private lastPortalUpdateT = -1;
  private lastPortalEye: [number, number, number] = [0, 0, 0];
  private lastPortalProj: Float32Array | null = null;
  private uPortalRgb: WebGLUniformLocation;
  private uPortalAlpha: WebGLUniformLocation;
  private uPortalScreen: WebGLUniformLocation;
  private uScreenSize: WebGLUniformLocation;
  private uTime: WebGLUniformLocation;
  private uTcGen: WebGLUniformLocation;
  private uTcGenS: WebGLUniformLocation;
  private uTcGenT: WebGLUniformLocation;
  private uDeformMode: WebGLUniformLocation;
  private uDeformVec: WebGLUniformLocation;
  private uDeformParams: WebGLUniformLocation;
  private uDeformPhase: WebGLUniformLocation;
  private uDeformFunc: WebGLUniformLocation;
  // Overlay (FX) program.
  private fxProgram: WebGLProgram;
  private fxProjView: WebGLUniformLocation;
  private fxModel: WebGLUniformLocation;
  private fxUvX: WebGLUniformLocation;
  private fxUvY: WebGLUniformLocation;
  private fxUvOff: WebGLUniformLocation;
  private fxRgb: WebGLUniformLocation;
  private fxAlpha: WebGLUniformLocation;
  private fxAlphaFunc: WebGLUniformLocation;
  private lightmapTex: WebGLTexture;
  /** Atlas upload result (size + failure reason) for `diag`. */
  private lightmapInfo: { w: number; h: number; error: string } = { w: 0, h: 0, error: "" };
  private whiteTex: WebGLTexture;
  private textures: Map<number, WebGLTexture>;
  private texCount: number;
  /** Count of texture uploads rejected by the driver (surfaces fall back). */
  private texFailed = 0;
  /** Textures registered with `clampmap` (CLAMP_TO_EDGE wrap). */
  private clampTextures = new Set<WebGLTexture>();
  /** Texture filtering quality + anisotropic filtering capability. */
  private textureMode: TextureMode = "anisotropic";
  private anisoExt: {
    TEXTURE_MAX_ANISOTROPY_EXT: number;
    MAX_TEXTURE_MAX_ANISOTROPY_EXT: number;
  } | null = null;
  private anisoMax = 1;
  private anisoLevel = 8;
  /** Draw chunks: base pass + additive/blended overlays. */
  private chunks: DrawChunk[] = [];
  /** Per-shader materials for PVS-visible runs (indexed by BSP shader id). */
  private materials: (DrawMaterial | null)[] = [];
  /** PVS-visible runs (flat shader/first/count triples), or null = draw all. */
  private visibleRuns: Uint32Array | null = null;
  private visibleRunCount = 0;
  // Dynamic visible-index buffer: concatenates the PVS runs per material so the
  // world pass issues one draw per material instead of one per run.
  private visVao: WebGLVertexArrayObject | null = null;
  private visIbo: WebGLBuffer | null = null;
  private mapIndices: Uint32Array | null = null;
  private visScratch: Uint32Array | null = null;
  private visGroups: Array<{ mat: DrawMaterial; start: number; count: number }> = [];
  // `WEBGL_multi_draw`: draw each material's PVS runs straight from the static
  // index buffer, skipping the per-frame index copy + `bufferSubData` entirely
  // (simpsons_q3 uploads 1-13 MB/frame through the dynamic buffer otherwise).
  private multiDrawSupported: MultiDrawExt | null = null;
  private multiDraw: MultiDrawExt | null = null;
  private visCounts = new Int32Array(0);
  private visOffsets = new Int32Array(0);

  // Skybox.
  private skyProgram: WebGLProgram | null = null;
  private skyProjViewLoc: WebGLUniformLocation | null = null;
  private skyEyeLoc: WebGLUniformLocation | null = null;
  private skyTexLoc: WebGLUniformLocation | null = null;
  private skyScaleLoc: WebGLUniformLocation | null = null;
  /** Cube-space scale for the skybox (so it fits inside the per-map far plane). */
  private skyScale = 1;
  private skyVao: WebGLVertexArrayObject | null = null;
  private skyVbo: WebGLBuffer | null = null;
  private skyIbo: WebGLBuffer | null = null;
  private skyFaceTextures: (WebGLTexture | null)[] = [];
  private skyRanges: Array<{ first: number; count: number; axis: number }> = [];

  // Runtime models (MD3): static map objects now, item/weapon models later.
  private modelProgram: WebGLProgram | null = null;
  private modelProjView: WebGLUniformLocation | null = null;
  private modelMatrixLoc: WebGLUniformLocation | null = null;
  private modelLerp: WebGLUniformLocation | null = null;
  private modelColor: WebGLUniformLocation | null = null;
  private modelHasTex: WebGLUniformLocation | null = null;
  private modelAlpha: WebGLUniformLocation | null = null;
  private modelEye: WebGLUniformLocation | null = null;
  private modelFogColor: WebGLUniformLocation | null = null;
  private modelFogDist: WebGLUniformLocation | null = null;
  private modelFogOn: WebGLUniformLocation | null = null;
  private models = new Map<number, GpuModel>();
  private modelCounter = 1;
  private modelInstances: ModelInstance[] = [];
  private viewModel: ModelInstance | null = null;
  private modelVao: WebGLVertexArrayObject | null = null;
  /** Client-local particle VFX layers, drawn after models (depth-tested) in order. */
  private particleSystems: ParticleSystem[] = [];
  /** Optional neon player trails, drawn after models and before particles. */
  private playerTrails: PlayerTrailSystem | null = null;
  /** Optional bloom post-process (renders the scene offscreen, then glows). */
  private bloom: Bloom | null = null;
  /** WebGL context losses since start (telemetry; a lost context blanks a frame). */
  private lostCount = 0;

  /** Surface flags. */
  static readonly SURF_SKY = 0x4;

  constructor(canvas: HTMLCanvasElement, lowPowerGpu = false, antialias = true) {
    const gl = canvas.getContext("webgl2", {
      antialias,
      depth: true,
      stencil: false,
      // `?gpu=low` lets the browser choose the adapter instead of forcing the
      // discrete GPU — a workaround for Firefox fullscreen GPU-process crashes on
      // hybrid-GPU (Optimus/PRIME) Linux setups.
      powerPreference: lowPowerGpu ? "default" : "high-performance",
    }) as WebGL2RenderingContext | null;
    if (!gl) throw new Error("WebGL2 not supported");
    this.gl = gl;
    // A lost context (common under GPU pressure, e.g. Firefox in fullscreen) would
    // otherwise leave a dead black canvas. Prevent the default so the browser can
    // restore it, and reload when it does — our GPU resources aren't re-uploaded
    // in place.
    canvas.addEventListener("webglcontextlost", (e) => {
      e.preventDefault();
      this.lostCount++;
      console.warn("[webrace] WebGL context lost");
    });
    canvas.addEventListener("webglcontextrestored", () => {
      console.warn("[webrace] WebGL context restored — reloading");
      location.reload();
    });

    // Anisotropic filtering (optional; falls back to trilinear if unsupported).
    const aniso = gl.getExtension("EXT_texture_filter_anisotropic") as {
      TEXTURE_MAX_ANISOTROPY_EXT: number;
      MAX_TEXTURE_MAX_ANISOTROPY_EXT: number;
    } | null;
    this.anisoExt = aniso;
    if (aniso) {
      this.anisoMax = gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT) as number;
    }

    // PVS multi-draw (Chrome/ANGLE). Without it the world pass rebuilds and
    // re-uploads the visible index list every frame; with it each material's
    // runs are drawn straight from the static map IBO.
    this.multiDrawSupported = gl.getExtension("WEBGL_multi_draw") as unknown as MultiDrawExt | null;
    this.multiDraw = this.multiDrawSupported;
    if (!this.multiDrawSupported) {
      console.info("[webrace] WEBGL_multi_draw unavailable: PVS uses the dynamic index buffer");
    }

    // Bloom post-process (the glow that makes bright VFX pop).
    this.bloom = new Bloom(gl);

    const vs = `#version 300 es
      layout(location=0) in vec3 a_pos;
      layout(location=1) in vec2 a_uv;
      layout(location=2) in vec2 a_lm;
      layout(location=3) in vec3 a_norm;
      layout(location=4) in vec4 a_color;
      layout(location=5) in vec3 a_center;
      uniform mat4 u_proj_view;
      uniform mat4 u_model;
      uniform float u_time;
      uniform vec3 u_eye;
      // deformVertexes
      uniform float u_deform_mode;
      uniform vec3 u_deform_vec;
      uniform vec4 u_deform_params;
      uniform float u_deform_phase;
      uniform float u_deform_func;
      out vec2 v_uv; out vec2 v_lm; out vec3 v_norm; out vec4 v_color; out vec3 v_world;
      float wavef(float x) {
        float f = x - floor(x);
        if (u_deform_func < 0.5) return sin(x * 6.2831853);
        if (u_deform_func < 1.5) return (f < 0.5) ? f * 2.0 : 2.0 - f * 2.0;
        if (u_deform_func < 2.5) return (f < 0.5) ? 1.0 : 0.0;
        if (u_deform_func < 3.5) return f;
        if (u_deform_func < 4.5) return 1.0 - f;
        return sin(x * 6.2831853);
      }
      void main() {
        vec3 pos = a_pos;
        if (u_deform_mode > 3.5) {
          // deformVertexes autosprite / autosprite2: billboard each face about
          // its centre (autosprite faces the viewer, autosprite2 about world Z).
          vec3 c = (u_model * vec4(a_center, 1.0)).xyz;
          vec3 rel = (u_model * vec4(pos, 1.0)).xyz - c;
          vec3 bb;
          if (u_deform_mode < 4.5) {
            vec3 vd = normalize(c - u_eye);
            vec3 refUp = abs(vd.z) > 0.99 ? vec3(0.0, 1.0, 0.0) : vec3(0.0, 0.0, 1.0);
            vec3 right = normalize(cross(refUp, vd));
            vec3 up = cross(vd, right);
            bb = c + right * dot(rel, right) + up * dot(rel, up);
          } else {
            vec3 vd = c - u_eye;
            vd.z = 0.0;
            float vl = length(vd);
            vd = vl > 0.001 ? vd / vl : vec3(1.0, 0.0, 0.0);
            vec3 right = vec3(-vd.y, vd.x, 0.0);
            vec3 up = vec3(0.0, 0.0, 1.0);
            bb = c + right * dot(rel, right) + up * dot(rel, up);
          }
          v_world = bb;
          gl_Position = u_proj_view * vec4(bb, 1.0);
          v_uv = a_uv; v_lm = a_lm; v_norm = a_norm; v_color = a_color;
          return;
        }
        if (u_deform_mode > 0.5 && u_deform_mode < 1.5) {
          // deformVertexes wave <div> <func> <base> <amp> <phase> <freq>
          float div = max(u_deform_params.w, 0.001);
          float x = (pos.x + pos.y + pos.z) / div + u_deform_params.y
                  + u_deform_phase + u_time * u_deform_params.z;
          pos += a_norm * (u_deform_params.x * wavef(x));
        } else if (u_deform_mode > 1.5 && u_deform_mode < 2.5) {
          // deformVertexes move <x y z> <func> <base> <amp> <phase> <freq>
          float x = u_deform_params.y + u_deform_phase + u_time * u_deform_params.z;
          pos += u_deform_vec * (u_deform_params.x * wavef(x));
        } else if (u_deform_mode > 2.5 && u_deform_mode < 3.5) {
          // deformVertexes normal <amp> <freq>
          pos += a_norm * (u_deform_params.x * sin(u_time * u_deform_params.y + u_deform_phase));
        } else if (u_deform_mode > 5.5) {
          // deformVertexes bulge <width> <height> <speed> (Q3 RB_CalcBulgeVertexes):
          // push along the surface normal, phased by the S texcoord + time.
          float b = u_deform_params.x
                  * sin(6.2831853 * (a_uv.x * u_deform_params.y + u_time * u_deform_params.z));
          pos += a_norm * b;
        }
        vec4 world = u_model * vec4(pos, 1.0);
        v_world = world.xyz;
        gl_Position = u_proj_view * world;
        v_uv = a_uv; v_lm = a_lm; v_norm = a_norm; v_color = a_color;
      }`;
    const fs = `#version 300 es
      precision highp float;
      in vec2 v_uv; in vec2 v_lm; in vec3 v_norm; in vec4 v_color; in vec3 v_world;
      uniform sampler2D u_texture;
      uniform sampler2D u_lightmap;
      uniform float u_has_texture;
      uniform float u_lit;
      uniform float u_wire;
      // tcMod affine (uv' = v_uv.x*u_uv_x + v_uv.y*u_uv_y + u_uv_off)
      uniform vec2 u_uv_x;
      uniform vec2 u_uv_y;
      uniform vec2 u_uv_off;
      // rgbGen / alphaGen
      uniform vec3 u_rgb;
      uniform float u_rgb_vertex;
      uniform float u_alpha;
      uniform float u_alpha_func;
      // fog / portal
      uniform vec3 u_eye;
      uniform vec3 u_fog_color;
      uniform float u_fog_dist;
      uniform float u_fog_on;
      uniform float u_fog_surface;
      uniform float u_portal_rgb;
      uniform float u_portal_alpha;
      // Portal surfaces sample the reflected view in screen space.
      uniform float u_portal_screen;
      uniform vec2 u_screen_size;
      // tcGen: 0 = affine tcMod, 1 = environment, 2 = lightmap, 3 = vector
      uniform float u_tcgen;
      uniform vec3 u_tcgen_s;
      uniform vec3 u_tcgen_t;
      out vec4 outColor;
      void main() {
        // Wireframe debug: flat bright lines, ignore texture/lightmap.
        if (u_wire > 0.5) {
          outColor = vec4(0.20, 1.0, 0.40, 1.0);
          return;
        }
        // Q3 fog volume surface: draw the fog colour with coverage growing with
        // distance instead of the shader's texture. (Without this a fog brush
        // is a flat grey/textured plane that hides everything behind it.)
        if (u_fog_surface > 0.5) {
          float d = length(v_world - u_eye);
          float f = clamp(d / max(u_fog_dist, 1.0), 0.0, 1.0);
          outColor = vec4(u_fog_color, f);
          return;
        }
        vec2 uv;
        if (u_portal_screen > 0.5) {
          uv = gl_FragCoord.xy / u_screen_size;
        } else if (u_tcgen > 2.5) {
          // tcGen vector: UV = dot(worldPos, S/T).
          uv = vec2(dot(v_world, u_tcgen_s), dot(v_world, u_tcgen_t));
        } else if (u_tcgen > 1.5) {
          uv = v_lm; // tcGen lightmap
        } else if (u_tcgen > 0.5) {
          // tcGen environment: reflection vector spherical mapping.
          vec3 viewDir = normalize(v_world - u_eye);
          vec3 r = reflect(viewDir, normalize(v_norm));
          uv = r.xy * 0.5 + 0.5;
        } else {
          uv = v_uv.x * u_uv_x + v_uv.y * u_uv_y + u_uv_off;
        }
        vec4 tex = texture(u_texture, uv);
        // Surface albedo (from texture) or a neutral gray fallback.
        vec3 albedo = mix(vec3(0.62), tex.rgb, u_has_texture);
        float alpha = mix(1.0, tex.a, u_has_texture);
        // rgbGen / alphaGen. u_rgb_vertex selects the interpolated vertex
        // color (rgbGen vertex/exactVertex).
        albedo *= u_rgb * mix(vec3(1.0), v_color.rgb, u_rgb_vertex);
        alpha *= u_alpha;
        // Q3 alphaFunc: alpha-test instead of blending (grates/foliage/banners).
        if (u_alpha_func > 0.5) {
          if (u_alpha_func < 1.5) {          // GT0
            if (alpha <= 0.0) discard;
          } else if (u_alpha_func < 2.5) {   // LT128
            if (alpha >= 0.5) discard;
          } else {                           // GE128
            if (alpha < 0.5) discard;
          }
        }
        float dist = length(v_world - u_eye);
        // portal rgbGen/alphaGen: fade with distance from the viewer.
        float pfade = clamp(1.0 - dist / 256.0, 0.0, 1.0);
        if (u_portal_rgb > 0.5) albedo *= pfade;
        if (u_portal_alpha > 0.5) alpha *= pfade;
        vec3 base;
        if (u_lit > 0.5) {
          // Baked lightmap: stored dark, meant to multiply the albedo.
          vec3 lm = texture(u_lightmap, v_lm).rgb;
          // Overbright in the style of Q3 (r_overbrightBits ~2) + gamma lift.
          base = pow(clamp(albedo * lm * 4.0, 0.0, 1.0), vec3(0.72));
        } else {
          base = albedo;
        }
        if (u_fog_on > 0.5) {
          base = mix(base, u_fog_color, clamp(dist / u_fog_dist, 0.0, 1.0));
        }
        outColor = vec4(base, alpha);
      }`;

    this.program = this.link(vs, fs);
    this.useProgram(this.program);

    this.uProjView = gl.getUniformLocation(this.program, "u_proj_view")!;
    this.uModel = gl.getUniformLocation(this.program, "u_model")!;
    this.uHasTexture = gl.getUniformLocation(this.program, "u_has_texture")!;
    this.uLit = gl.getUniformLocation(this.program, "u_lit")!;
    this.uWire = gl.getUniformLocation(this.program, "u_wire")!;
    this.uUvX = gl.getUniformLocation(this.program, "u_uv_x")!;
    this.uUvY = gl.getUniformLocation(this.program, "u_uv_y")!;
    this.uUvOff = gl.getUniformLocation(this.program, "u_uv_off")!;
    this.uRgb = gl.getUniformLocation(this.program, "u_rgb")!;
    this.uRgbVertex = gl.getUniformLocation(this.program, "u_rgb_vertex")!;
    this.uAlpha = gl.getUniformLocation(this.program, "u_alpha")!;
    this.uAlphaFunc = gl.getUniformLocation(this.program, "u_alpha_func")!;
    this.uEye = gl.getUniformLocation(this.program, "u_eye")!;
    this.uFogColor = gl.getUniformLocation(this.program, "u_fog_color")!;
    this.uFogDist = gl.getUniformLocation(this.program, "u_fog_dist")!;
    this.uFogOn = gl.getUniformLocation(this.program, "u_fog_on")!;
    this.uFogSurface = gl.getUniformLocation(this.program, "u_fog_surface")!;
    this.uPortalRgb = gl.getUniformLocation(this.program, "u_portal_rgb")!;
    this.uPortalAlpha = gl.getUniformLocation(this.program, "u_portal_alpha")!;
    this.uPortalScreen = gl.getUniformLocation(this.program, "u_portal_screen")!;
    this.uScreenSize = gl.getUniformLocation(this.program, "u_screen_size")!;
    this.uTime = gl.getUniformLocation(this.program, "u_time")!;
    this.uTcGen = gl.getUniformLocation(this.program, "u_tcgen")!;
    this.uTcGenS = gl.getUniformLocation(this.program, "u_tcgen_s")!;
    this.uTcGenT = gl.getUniformLocation(this.program, "u_tcgen_t")!;
    this.uDeformMode = gl.getUniformLocation(this.program, "u_deform_mode")!;
    this.uDeformVec = gl.getUniformLocation(this.program, "u_deform_vec")!;
    this.uDeformParams = gl.getUniformLocation(this.program, "u_deform_params")!;
    this.uDeformPhase = gl.getUniformLocation(this.program, "u_deform_phase")!;
    this.uDeformFunc = gl.getUniformLocation(this.program, "u_deform_func")!;
    const uLightmap = gl.getUniformLocation(this.program, "u_lightmap");
    const uTexture = gl.getUniformLocation(this.program, "u_texture");
    if (uLightmap) gl.uniform1i(uLightmap, 0);
    if (uTexture) gl.uniform1i(uTexture, 1);
    // Sensible defaults (identity tcMod, white rgb, opaque, no fog/portal).
    this.u2(this.uUvX, 1, 0);
    this.u2(this.uUvY, 0, 1);
    this.u2(this.uUvOff, 0, 0);
    this.u3(this.uRgb, 1, 1, 1);
    this.u1(this.uRgbVertex, 0);
    this.u1(this.uAlpha, 1);
    this.u1(this.uAlphaFunc, 0);
    this.u1(this.uFogOn, 0);
    this.u1(this.uFogSurface, 0);
    this.u1(this.uPortalRgb, 0);
    this.u1(this.uPortalAlpha, 0);
    this.u1(this.uPortalScreen, 0);
    this.u2(this.uScreenSize, 1, 1);
    this.u1(this.uTcGen, 0);
    this.u1(this.uDeformMode, 0);
    this.u3(this.uDeformVec, 0, 0, 0);
    gl.uniform4f(this.uDeformParams, 0, 0, 0, 1);
    this.u1(this.uDeformPhase, 0);
    this.u1(this.uDeformFunc, 0);
    this.u1(this.uTime, 0);

    // Overlay (FX) program for additive/blended shader stages.
    const fxVs = `#version 300 es
      layout(location=0) in vec3 a_pos;
      layout(location=1) in vec2 a_uv;
      layout(location=4) in vec4 a_color;
      uniform mat4 u_proj_view;
      uniform mat4 u_model;
      out vec2 v_uv; out vec4 v_color;
      void main() {
        gl_Position = u_proj_view * u_model * vec4(a_pos, 1.0);
        v_uv = a_uv; v_color = a_color;
      }`;
    const fxFs = `#version 300 es
      precision highp float;
      in vec2 v_uv; in vec4 v_color;
      uniform sampler2D u_texture;
      uniform vec2 u_uv_x;
      uniform vec2 u_uv_y;
      uniform vec2 u_uv_off;
      uniform vec3 u_rgb;
      uniform float u_alpha;
      uniform float u_alpha_func;
      out vec4 outColor;
      void main() {
        vec2 uv = v_uv.x * u_uv_x + v_uv.y * u_uv_y + u_uv_off;
        vec4 tex = texture(u_texture, uv);
        float a = tex.a * v_color.a * u_alpha;
        if (u_alpha_func > 0.5) {
          if (u_alpha_func < 1.5) { if (a <= 0.0) discard; }
          else if (u_alpha_func < 2.5) { if (a >= 0.5) discard; }
          else { if (a < 0.5) discard; }
        }
        outColor = tex * v_color * vec4(u_rgb, u_alpha);
      }`;
    this.fxProgram = this.link(fxVs, fxFs);
    this.fxProjView = gl.getUniformLocation(this.fxProgram, "u_proj_view")!;
    this.fxModel = gl.getUniformLocation(this.fxProgram, "u_model")!;
    this.fxUvX = gl.getUniformLocation(this.fxProgram, "u_uv_x")!;
    this.fxUvY = gl.getUniformLocation(this.fxProgram, "u_uv_y")!;
    this.fxUvOff = gl.getUniformLocation(this.fxProgram, "u_uv_off")!;
    this.fxRgb = gl.getUniformLocation(this.fxProgram, "u_rgb")!;
    this.fxAlpha = gl.getUniformLocation(this.fxProgram, "u_alpha")!;
    this.fxAlphaFunc = gl.getUniformLocation(this.fxProgram, "u_alpha_func")!;
    this.useProgram(this.fxProgram);
    const fxTex = gl.getUniformLocation(this.fxProgram, "u_texture");
    if (fxTex) gl.uniform1i(fxTex, 1);
    this.u2(this.fxUvX, 1, 0);
    this.u2(this.fxUvY, 0, 1);
    this.u2(this.fxUvOff, 0, 0);
    this.u3(this.fxRgb, 1, 1, 1);
    this.u1(this.fxAlpha, 1);
    this.u1(this.fxAlphaFunc, 0);
    this.useProgram(this.program);

    // Lightmap atlas texture (unit 0). Kept unfiltered-by-mode and without
    // mipmaps: the atlas is a grid of independent cells, so mips would bleed
    // light between them. CLAMP avoids sampling past the atlas edge.
    this.lightmapTex = gl.createTexture()!;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.lightmapTex);
    this.configureLightmap();

    // Texture cache (unit 1): shader-index -> WebGLTexture.
    this.textures = new Map();
    this.texCount = 0;
    // Default 1x1 white texture for faces whose image failed to load.
    this.whiteTex = gl.createTexture()!;
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, this.whiteTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([158, 158, 158, 255]));
    gl.generateMipmap(gl.TEXTURE_2D);
    this.configureTex(false);

    this.vao = gl.createVertexArray()!;
    this.vbo = gl.createBuffer()!;
    this.ibo = gl.createBuffer()!;
    this.wireIbo = gl.createBuffer()!;
    this.wireVao = gl.createVertexArray()!;

    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    this.setupAttribs();
    gl.bindVertexArray(null);

    gl.bindVertexArray(this.wireVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.wireIbo);
    this.setupAttribs();
    gl.bindVertexArray(null);

    gl.enable(gl.DEPTH_TEST);
    // Culling stays off for now. BSP faces are wound CCW as seen from their
    // visible side (checked against the stored face normals across several
    // maps), so `cullFace(BACK)` + `frontFace(CCW)` is the right pairing — but
    // models/particles/viewmodel and the reflected portal FBO use different
    // conventions, so enabling it globally needs per-pass handling (future
    // work). The cel-outline shells are skipped at material build time
    // (`def.outlineShell`) instead.
    // gl.enable(gl.CULL_FACE);
    gl.clearColor(0.42, 0.58, 0.80, 1);
  }

  /** Set the interleaved 17-float vertex attributes on the bound VAO. */
  private setupAttribs() {
    const gl = this.gl;
    const stride = 17 * 4;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, stride, 12);
    gl.enableVertexAttribArray(2);
    gl.vertexAttribPointer(2, 2, gl.FLOAT, false, stride, 20);
    gl.enableVertexAttribArray(3);
    gl.vertexAttribPointer(3, 3, gl.FLOAT, false, stride, 28);
    gl.enableVertexAttribArray(4);
    gl.vertexAttribPointer(4, 4, gl.FLOAT, false, stride, 40);
    // a_center (face centre, for deformVertexes autosprite)
    gl.enableVertexAttribArray(5);
    gl.vertexAttribPointer(5, 3, gl.FLOAT, false, stride, 56);
  }

  /** Enable/disable wireframe (debug) rendering. */
  setWireframe(on: boolean) {
    this.wireframe = on;
  }
  isWireframe(): boolean {
    return this.wireframe;
  }

  private link(vs: string, fs: string): WebGLProgram {
    const gl = this.gl;
    const mk = (t: number, s: string) => {
      const sh = gl.createShader(t)!;
      gl.shaderSource(sh, s);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        throw new Error("shader: " + gl.getShaderInfoLog(sh));
      }
      return sh;
    };
    const p = gl.createProgram()!;
    gl.attachShader(p, mk(gl.VERTEX_SHADER, vs));
    gl.attachShader(p, mk(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      throw new Error("link: " + gl.getProgramInfoLog(p));
    }
    return p;
  }

  /** Upload a map's vertex + index data (from WASM memory via views). */
  uploadMap(
    memory: WebAssembly.Memory,
    vertsPtr: number,
    vertCount: number,
    idxPtr: number,
    idxCount: number,
  ) {
    const gl = this.gl;
    gl.bindVertexArray(this.vao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    const v = new Float32Array(memory.buffer, vertsPtr, vertCount);
    gl.bufferData(gl.ARRAY_BUFFER, v, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.ibo);
    const idx = new Uint32Array(memory.buffer, idxPtr, idxCount);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
    this.indexCount = idxCount;

    // Dynamic visible-index buffer for the PVS world pass (fallback only — the
    // `WEBGL_multi_draw` path draws straight from the static IBO). The CPU copy
    // + GPU buffer are one map's worth of indices each (43 MB on
    // simpsons_q3), so don't allocate them when multi-draw is available.
    if (this.multiDraw) {
      this.mapIndices = null;
      this.visScratch = null;
      if (this.visIbo) {
        gl.deleteBuffer(this.visIbo);
        this.visIbo = null;
      }
      if (this.visVao) {
        gl.deleteVertexArray(this.visVao);
        this.visVao = null;
      }
    } else {
      this.mapIndices = new Uint32Array(idx);
      this.visScratch = new Uint32Array(idxCount);
      if (!this.visIbo) this.visIbo = gl.createBuffer();
      if (!this.visVao) {
        this.visVao = gl.createVertexArray();
        gl.bindVertexArray(this.visVao);
        gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
        this.setupAttribs();
      } else {
        gl.bindVertexArray(this.visVao);
      }
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.visIbo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idxCount * 4, gl.DYNAMIC_DRAW);
      gl.bindVertexArray(null);
    }

    // Wireframe line indices (2x the triangle indices, emitted in the same
    // order so every triangle range maps to `[first*2, count*2)`). Bind the
    // line VAO *before* touching the line IBO, otherwise the element-buffer
    // binding would be recorded into `this.vao` and normal draws would use the
    // line indices.
    const lines = buildLineIndices(idx);
    gl.bindVertexArray(this.wireVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.vbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.wireIbo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, lines, gl.STATIC_DRAW);
    this.setupAttribs();
    this.wireIndexCount = lines.length;
    gl.bindVertexArray(null);
  }

  /**
   * Upload the moving brush entities' geometry into a second vertex/index
   * buffer. The same interleaved 14-float layout as `uploadMap`.
   */
  uploadMovers(
    memory: WebAssembly.Memory,
    vertsPtr: number,
    vertCount: number,
    idxPtr: number,
    idxCount: number,
  ) {
    const gl = this.gl;
    if (!this.moverVao) {
      this.moverVao = gl.createVertexArray();
      this.moverVbo = gl.createBuffer();
      this.moverIbo = gl.createBuffer();
      this.moverWireIbo = gl.createBuffer();
      this.moverWireVao = gl.createVertexArray();
    }
    if (idxCount === 0 || vertCount === 0) {
      this.moverIndexCount = 0;
      return;
    }
    gl.bindVertexArray(this.moverVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.moverVbo);
    const v = new Float32Array(memory.buffer, vertsPtr, vertCount);
    gl.bufferData(gl.ARRAY_BUFFER, v, gl.STATIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.moverIbo);
    const idx = new Uint32Array(memory.buffer, idxPtr, idxCount);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, idx, gl.STATIC_DRAW);
    this.setupAttribs();
    this.moverIndexCount = idxCount;

    const lines = buildLineIndices(idx);
    // Bind the wire VAO before the wire IBO so `moverVao` keeps its triangle IBO.
    gl.bindVertexArray(this.moverWireVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.moverVbo);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.moverWireIbo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, lines, gl.STATIC_DRAW);
    this.setupAttribs();
    gl.bindVertexArray(null);
  }

  /** Set the mover draw chunks (each references a mover index for its matrix). */
  setMoverChunks(chunks: MoverChunk[]) {
    this.moverChunks = chunks;
  }

  /** Set the portal-surface chunks (each drawn with a reflected view texture). */
  setPortalChunks(chunks: PortalChunk[]) {
    // Drop FBOs for portal chunks that are gone.
    for (const key of [...this.portalFbos.keys()]) {
      if (!chunks.includes(key)) {
        const f = this.portalFbos.get(key)!;
        this.gl.deleteFramebuffer(f.fb);
        this.gl.deleteTexture(f.color);
        this.gl.deleteRenderbuffer(f.depth);
        this.portalFbos.delete(key);
      }
    }
    this.portalChunks = chunks;
  }

  /** Upload the packed RGB lightmap atlas (from WASM memory). */
  uploadLightmap(memory: WebAssembly.Memory, ptr: number, len: number, w: number, h: number) {
    const gl = this.gl;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.lightmapTex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    this.lightmapInfo = { w, h, error: "" };
    // A failed/unusable atlas makes every lit world surface sample black — the
    // map then renders as "black with glowing unlit bits" (sky/items/models),
    // which is easy to mistake for a broken map. Detect it and fall back to a
    // neutral 0.25 grey tile: the shader does `albedo * lm * 4`, so grey keeps
    // the map fully lit and readable instead of black.
    const max = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    let error = "";
    if (!(w > 0 && h > 0)) error = `empty atlas ${w}x${h}`;
    else if (w > max || h > max) error = `atlas ${w}x${h} > MAX_TEXTURE_SIZE ${max}`;
    else if (len < w * h * 3) error = `atlas ${w}x${h} needs ${w * h * 3} bytes, got ${len}`;
    if (!error) {
      // Drain stale errors so the check below only sees this upload.
      for (let i = 0; i < 8 && gl.getError() !== gl.NO_ERROR; i++) { /* drain */ }
      gl.texImage2D(
        gl.TEXTURE_2D, 0, gl.RGB, w, h, 0, gl.RGB, gl.UNSIGNED_BYTE,
        new Uint8Array(memory.buffer, ptr, len),
      );
      const e = gl.getError();
      if (e !== gl.NO_ERROR) error = `texImage2D ${w}x${h} failed (0x${e.toString(16)})`;
    }
    if (error) {
      console.warn(`[lightmap] ${error}; using flat grey (unlit) fallback`);
      gl.texImage2D(
        gl.TEXTURE_2D, 0, gl.RGB, 1, 1, 0, gl.RGB, gl.UNSIGNED_BYTE,
        new Uint8Array([64, 64, 64]),
      );
      this.lightmapInfo.error = error;
    }
  }

  /** Atlas upload state for `diag` (size + failure reason, `""` = ok). */
  getLightmapInfo(): { w: number; h: number; error: string } {
    return this.lightmapInfo;
  }

  /** Texture uploads the driver rejected (surfaces use the fallback). */
  getTexFailed(): number {
    return this.texFailed;
  }

  resize(w: number, h: number) {
    this.canvasW = w;
    this.canvasH = h;
    this.gl.viewport(0, 0, w, h);
    this.bloom?.resize(w, h);
  }

  /** Bloom post-process controls (Debug tab / URL options). */
  getBloom(): Bloom | null {
    return this.bloom;
  }

  /** WebGL context losses since start (telemetry: a loss blanks a frame). */
  contextLostCount(): number {
    return this.lostCount;
  }

  /** Enable the panini composite warp (visual-only). */
  setPanini(on: boolean, hfovRad: number, vfovRad: number, d = 1): void {
    this.bloom?.setPanini(on, hfovRad, vfovRad, d);
  }

  /** Set the draw chunks (base pass + overlays) for this map. */
  setChunks(chunks: DrawChunk[]) {
    this.chunks = chunks;
  }

  /** Set per-shader materials for PVS-visible runs (null = skip / portal). */
  setMaterials(materials: (DrawMaterial | null)[]) {
    this.materials = materials;
  }

  /**
   * Set this frame's PVS-visible draw runs as flat `(shader, first, count)`
   * triples, or `null` to draw every chunk (fallback / no visibility data).
   */
  setVisibleRuns(runs: Uint32Array | null, count = 0) {
    this.visibleRuns = runs;
    this.visibleRunCount = count;
  }

  /** A/B switch (`?multidraw=0`): force the dynamic `visIbo` PVS path. */
  setMultiDrawEnabled(on: boolean) {
    this.multiDraw = on ? this.multiDrawSupported : null;
  }

  /** Whether the PVS run path is using `WEBGL_multi_draw` (no index upload). */
  get multiDrawActive(): boolean {
    return this.multiDraw !== null;
  }

  /**
   * Concatenate this frame's visible index runs into `visIbo`, grouped by
   * material. One draw call per material instead of one per run (13octo: 339
   * runs → 38 groups), which is the difference between ~9ms and a few ms of
   * CPU draw submission on big open maps.
   *
   * With `WEBGL_multi_draw` this only records each run's `(count, byte offset)`
   * — the runs are then drawn straight from the static IBO, so no index copy
   * and no upload happen at all.
   */
  private rebuildVisibleGroups() {
    const runs = this.visibleRuns;
    if (!runs) return;
    const n = this.visibleRunCount;
    const groups = this.visGroups;
    groups.length = 0;

    if (this.multiDraw) {
      if (this.visCounts.length < n) {
        this.visCounts = new Int32Array(n);
        this.visOffsets = new Int32Array(n);
      }
      const counts = this.visCounts;
      const offsets = this.visOffsets;
      let o = 0;
      let r = 0;
      while (r < n) {
        const mat = this.materials[runs[r * 3]];
        if (!mat) {
          r++;
          continue;
        }
        const start = o;
        let e = r;
        while (e < n && this.materials[runs[e * 3]] === mat) {
          counts[o] = runs[e * 3 + 2];
          offsets[o] = runs[e * 3 + 1] * 4; // byte offset into the static IBO
          o++;
          e++;
        }
        if (o > start) groups.push({ mat, start, count: o - start });
        r = e;
      }
      return;
    }

    const map = this.mapIndices;
    const scratch = this.visScratch;
    if (!map || !scratch || !this.visVao || !this.visIbo) return;
    let o = 0;
    let r = 0;
    while (r < n) {
      const mat = this.materials[runs[r * 3]];
      if (!mat) {
        r++;
        continue;
      }
      const start = o;
      let e = r;
      while (e < n && this.materials[runs[e * 3]] === mat) {
        const first = runs[e * 3 + 1];
        const count = runs[e * 3 + 2];
        if (o + count > scratch.length) break;
        scratch.set(map.subarray(first, first + count), o);
        o += count;
        e++;
      }
      if (o > start) groups.push({ mat, start, count: o - start });
      r = e;
    }
    const gl = this.gl;
    gl.bindVertexArray(this.visVao);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.visIbo);
    if (o > 0) gl.bufferSubData(gl.ELEMENT_ARRAY_BUFFER, 0, scratch.subarray(0, o));
  }

  /** Pick the current animmap frame texture id for `time` seconds. */
  private pickAnim(ids: number[], freq: number, time: number): number | null {
    if (!ids.length) return null;
    if (ids.length === 1 || freq <= 0) return ids[0];
    const frame = Math.floor(time * freq) % ids.length;
    return ids[frame];
  }

  /** `?cull=0` disables face culling globally (A/B screenshots). */
  private cullingEnabled = true;
  setCullingEnabled(on: boolean) {
    this.cullingEnabled = on;
    this.curCull = "";
    if (!on) this.gl.disable(this.gl.CULL_FACE);
  }

  /**
   * Apply a material's cull mode. `frontFace` stays CCW (WebGL default); the
   * BSP render buffer is wound so a surface's visible side is a GL *back* face,
   * hence the `front` default (cull front faces, keep the visible back ones).
   * Mirrored views (portal FBOs) flip the projected winding, so swap sides.
   */
  private setCull(mode: CullMode, mirrored = false) {
    const gl = this.gl;
    const key = !this.cullingEnabled || mode === "none" ? "off" : `${mirrored ? "m" : "n"}:${mode}`;
    if (this.curCull === key) return;
    this.curCull = key;
    if (!this.cullingEnabled || mode === "none") {
      gl.disable(gl.CULL_FACE);
      return;
    }
    gl.enable(gl.CULL_FACE);
    const side = mode === "back" ? gl.BACK : gl.FRONT;
    gl.cullFace(mirrored ? (side === gl.BACK ? gl.FRONT : gl.BACK) : side);
  }

  private blendFactor(f: BlendFactor): number {
    const gl = this.gl;
    switch (f) {
      case "zero": return gl.ZERO;
      case "one": return gl.ONE;
      case "srcColor": return gl.SRC_COLOR;
      case "oneMinusSrcColor": return gl.ONE_MINUS_SRC_COLOR;
      case "srcAlpha": return gl.SRC_ALPHA;
      case "oneMinusSrcAlpha": return gl.ONE_MINUS_SRC_ALPHA;
      case "dstColor": return gl.DST_COLOR;
      case "oneMinusDstColor": return gl.ONE_MINUS_DST_COLOR;
      case "dstAlpha": return gl.DST_ALPHA;
      case "oneMinusDstAlpha": return gl.ONE_MINUS_DST_ALPHA;
    }
  }

  private setBlend(mode: BlendMode) {
    const gl = this.gl;
    if (this.curBlend === mode) return;
    this.curBlend = mode;
    if (mode === "opaque") {
      gl.disable(gl.BLEND);
      return;
    }
    gl.enable(gl.BLEND);
    if (typeof mode === "object") {
      gl.blendFunc(this.blendFactor(mode.src), this.blendFactor(mode.dst));
      return;
    }
    if (mode === "add") gl.blendFunc(gl.ONE, gl.ONE);
    else if (mode === "addAlpha") gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
    else if (mode === "blend") gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    else if (mode === "filter") gl.blendFunc(gl.DST_COLOR, gl.ZERO);
    else if (mode === "blacken") gl.blendFunc(gl.ZERO, gl.ONE_MINUS_SRC_COLOR);
  }

  private useProgram(p: WebGLProgram | null) {
    if (!p || this.curProgram === p) return;
    this.curProgram = p;
    this.gl.useProgram(p);
  }

  /**
   * Drop the cached GL state. External passes (the particle systems) call
   * `gl.useProgram`/`depthMask`/`blendFunc` directly, so the caches no longer
   * describe the bound state. Without this the viewmodel drawn after the
   * particles was bound to the *particle* program (the `useProgram(modelProgram)`
   * was skipped as "already current") and vanished for as long as any particle
   * lived — e.g. the gun disappearing on every shot.
   */
  private invalidateGlState() {
    this.curProgram = null;
    this.curBlend = null;
    this.curDepthMask = null;
    this.curDepthFunc = null;
    this.curCull = "";
  }

  private setDepthMask(v: boolean) {
    if (this.curDepthMask === v) return;
    this.curDepthMask = v;
    this.gl.depthMask(v);
  }

  private setDepthFunc(v: number) {
    if (this.curDepthFunc === v) return;
    this.curDepthFunc = v;
    this.gl.depthFunc(v);
  }

  /** Uniform value caches: uniform1f was 17.6% of a 13octo trace. */
  private uniCache1 = new Map<WebGLUniformLocation, number>();
  private uniCache2 = new Map<WebGLUniformLocation, [number, number]>();
  private uniCache3 = new Map<WebGLUniformLocation, [number, number, number]>();

  // GL state cache (reset at the start of each frame). Avoids redundant
  // enable/disable/blendFunc/depthMask/depthFunc/useProgram calls, which were
  // ~16% of a 13octo trace after uniform caching.
  private curProgram: WebGLProgram | null = null;
  private curBlend: BlendMode | null = null;
  private curDepthMask: boolean | null = null;
  private curDepthFunc: number | null = null;
  private curCull = "";

  private u1(loc: WebGLUniformLocation | null, v: number) {
    if (!loc) return;
    if (this.uniCache1.get(loc) === v) return;
    this.uniCache1.set(loc, v);
    this.gl.uniform1f(loc, v);
  }
  private u2(loc: WebGLUniformLocation | null, a: number, b: number) {
    if (!loc) return;
    const c = this.uniCache2.get(loc);
    if (c && c[0] === a && c[1] === b) return;
    if (c) { c[0] = a; c[1] = b; } else this.uniCache2.set(loc, [a, b]);
    this.gl.uniform2f(loc, a, b);
  }
  private u3(loc: WebGLUniformLocation | null, a: number, b: number, c3: number) {
    if (!loc) return;
    const c = this.uniCache3.get(loc);
    if (c && c[0] === a && c[1] === b && c[2] === c3) return;
    if (c) { c[0] = a; c[1] = b; c[2] = c3; } else this.uniCache3.set(loc, [a, b, c3]);
    this.gl.uniform3f(loc, a, b, c3);
  }

  /**
   * Build the skybox from 6 face images in Q3 suffix order
   * `[rt, bk, lf, ft, up, dn]` (matching Q3's `suf[]` / `MakeSkyVec`).
   */
  /** Scale the skybox cube so it stays inside the current far plane. */
  setSkyboxScale(scale: number) {
    this.skyScale = Number.isFinite(scale) && scale > 0 ? scale : 1;
  }

  setSkybox(faces: Array<{ width: number; height: number; data: Uint8ClampedArray | Uint8Array } | null>) {
    const gl = this.gl;
    if (!this.skyProgram) {
      const vs = `#version 300 es
        layout(location=0) in vec3 a_pos;
        layout(location=1) in vec2 a_uv;
        uniform mat4 u_proj_view;
        uniform vec3 u_eye;
        uniform float u_sky_scale;
        out vec2 v_uv;
        void main() {
          // Draw at the far plane in clip space (z = w). The cube's *corners*
          // sit sqrt(3) further out than its faces, so sizing it to fit inside
          // the per-map far plane clipped the corners — the clipped sextants
          // showed the clear colour (light blue) as a patch that followed the
          // view. Forcing z = w makes the sky depth-independent: never clipped
          // by far, never in front of world geometry.
          vec4 p = u_proj_view * vec4(a_pos * u_sky_scale + u_eye, 1.0);
          gl_Position = vec4(p.xy, p.w, p.w);
          v_uv = a_uv;
        }`;
      const fs = `#version 300 es
        precision highp float;
        in vec2 v_uv;
        uniform sampler2D u_tex;
        out vec4 outColor;
        void main() { outColor = vec4(texture(u_tex, v_uv).rgb, 1.0); }`;
      this.skyProgram = this.link(vs, fs);
      // Cache uniform locations (avoids getUniformLocation every frame).
      this.skyProjViewLoc = gl.getUniformLocation(this.skyProgram, "u_proj_view");
      this.skyEyeLoc = gl.getUniformLocation(this.skyProgram, "u_eye");
      this.skyTexLoc = gl.getUniformLocation(this.skyProgram, "u_tex");
      this.skyScaleLoc = gl.getUniformLocation(this.skyProgram, "u_sky_scale");
      this.skyVao = gl.createVertexArray();
      this.skyVbo = gl.createBuffer();
      this.skyIbo = gl.createBuffer();
    }

    // Q3 `st_to_vec` / suffix order.
    const stToVec = [
      [3, -1, 2],
      [-3, 1, 2],
      [1, 3, 2],
      [-1, -3, 2],
      [-2, -1, 3],
      [2, -1, -3],
    ];
    const SCALE = 80000;
    const corners = [
      [-1, -1],
      [1, -1],
      [1, 1],
      [-1, 1],
    ];
    const verts: number[] = [];
    const idx: number[] = [];
    this.skyRanges = [];
    for (let axis = 0; axis < 6; axis++) {
      const base = verts.length / 5;
      for (const [s, t] of corners) {
        const b = [s, t, 1];
        const out = [0, 0, 0];
        for (let j = 0; j < 3; j++) {
          const k = stToVec[axis][j];
          out[j] = (k < 0 ? -b[-k - 1] : b[k - 1]) * SCALE;
        }
        // The wall faces (rt/bk/lf/ft = axes 0..3) need their V inverted so the
        // sky image's top lands on the wall top (the up/dn faces are already
        // correct with the direct mapping).
        const v = axis < 4 ? 1 - (t + 1) * 0.5 : (t + 1) * 0.5;
        verts.push(out[0], out[1], out[2], (s + 1) * 0.5, v);
      }
      const first = idx.length;
      idx.push(base, base + 1, base + 2, base, base + 2, base + 3);
      this.skyRanges.push({ first, count: 6, axis });
    }

    gl.bindVertexArray(this.skyVao);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.skyVbo);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(verts), gl.STATIC_DRAW);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, this.skyIbo);
    gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint32Array(idx), gl.STATIC_DRAW);
    const stride = 5 * 4;
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 3, gl.FLOAT, false, stride, 0);
    gl.enableVertexAttribArray(1);
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, stride, 12);
    gl.bindVertexArray(null);

    // Textures (one per face).
    gl.activeTexture(gl.TEXTURE2);
    this.skyFaceTextures = faces.map((img) => {
      if (!img) return null;
      const tex = gl.createTexture()!;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, img.width, img.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, img.data);
      gl.generateMipmap(gl.TEXTURE_2D);
      this.configureTex(false);
      return tex;
    });
  }

  /** Remove any skybox (e.g. when loading a map that has none). */
  clearSkybox() {
    const gl = this.gl;
    for (const t of this.skyFaceTextures) {
      if (t) gl.deleteTexture(t);
    }
    this.skyFaceTextures = [];
    this.skyRanges = [];
  }

  /** Lazily create the runtime-model program (MD3 positions + texcoords). */
  private ensureModelProgram() {
    if (this.modelProgram) return;
    const gl = this.gl;
    const vs = `#version 300 es
      layout(location=0) in vec3 a_pos0;
      layout(location=1) in vec3 a_pos1;
      layout(location=2) in vec2 a_uv;
      uniform mat4 u_proj_view;
      uniform mat4 u_model;
      uniform float u_lerp;
      out vec2 v_uv;
      out vec3 v_world;
      void main() {
        vec3 p = mix(a_pos0, a_pos1, u_lerp);
        vec4 w = u_model * vec4(p, 1.0);
        v_world = w.xyz;
        gl_Position = u_proj_view * w;
        v_uv = a_uv;
      }`;
    const fs = `#version 300 es
      precision highp float;
      in vec2 v_uv;
      in vec3 v_world;
      uniform sampler2D u_texture;
      uniform vec3 u_color;
      uniform float u_has_texture;
      uniform float u_alpha;
      uniform vec3 u_eye;
      uniform vec3 u_fog_color;
      uniform float u_fog_dist;
      uniform float u_fog_on;
      out vec4 outColor;
      void main() {
        vec4 tex = texture(u_texture, v_uv);
        vec3 c = mix(vec3(1.0), tex.rgb, u_has_texture) * u_color;
        if (u_fog_on > 0.5) {
          float d = length(v_world - u_eye);
          c = mix(c, u_fog_color, clamp(d / max(u_fog_dist, 1.0), 0.0, 1.0));
        }
        outColor = vec4(c, u_alpha);
      }`;
    this.modelProgram = this.link(vs, fs);
    this.modelVao = gl.createVertexArray();
    this.modelProjView = gl.getUniformLocation(this.modelProgram, "u_proj_view");
    this.modelMatrixLoc = gl.getUniformLocation(this.modelProgram, "u_model");
    this.modelLerp = gl.getUniformLocation(this.modelProgram, "u_lerp");
    this.modelColor = gl.getUniformLocation(this.modelProgram, "u_color");
    this.modelHasTex = gl.getUniformLocation(this.modelProgram, "u_has_texture");
    this.modelAlpha = gl.getUniformLocation(this.modelProgram, "u_alpha");
    this.modelEye = gl.getUniformLocation(this.modelProgram, "u_eye");
    this.modelFogColor = gl.getUniformLocation(this.modelProgram, "u_fog_color");
    this.modelFogDist = gl.getUniformLocation(this.modelProgram, "u_fog_dist");
    this.modelFogOn = gl.getUniformLocation(this.modelProgram, "u_fog_on");
    this.useProgram(this.modelProgram);
    const tex = gl.getUniformLocation(this.modelProgram, "u_texture");
    if (tex) gl.uniform1i(tex, 1);
  }

  /** Upload a parsed MD3 model, one texture id per surface. Returns its id. */
  uploadModel(md3: Md3Model, texIds: (number | null)[]): number {
    const gl = this.gl;
    const surfaces: GpuModelSurface[] = md3.surfaces.map((s, i) => {
      const posVbo = gl.createBuffer()!;
      gl.bindBuffer(gl.ARRAY_BUFFER, posVbo);
      gl.bufferData(gl.ARRAY_BUFFER, s.positions, gl.STATIC_DRAW);
      const stVbo = gl.createBuffer()!;
      gl.bindBuffer(gl.ARRAY_BUFFER, stVbo);
      gl.bufferData(gl.ARRAY_BUFFER, s.st, gl.STATIC_DRAW);
      const ibo = gl.createBuffer()!;
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, ibo);
      gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, s.indices, gl.STATIC_DRAW);
      const id = texIds[i] ?? null;
      const per = s.numVerts * 3;
      const frameSums: number[] = [];
      for (let f = 0; f < Math.max(1, s.numFrames); f++) {
        let sum = 0;
        for (let k = 0; k < per; k++) sum += s.positions[f * per + k] || 0;
        frameSums.push(Math.round(sum * 100) / 100);
      }
      return {
        posVbo,
        stVbo,
        ibo,
        indexCount: s.indices.length,
        numVerts: s.numVerts,
        numFrames: Math.max(1, s.numFrames),
        tex: id !== null ? this.textures.get(id) ?? null : null,
        positionsLen: s.positions.length,
        frameSums,
      };
    });
    const model = this.modelCounter++;
    this.models.set(model, { surfaces });
    return model;
  }

  /** Free all uploaded models (called when loading a new map). */
  clearModels() {
    const gl = this.gl;
    for (const m of this.models.values()) {
      for (const s of m.surfaces) {
        gl.deleteBuffer(s.posVbo);
        gl.deleteBuffer(s.stVbo);
        gl.deleteBuffer(s.ibo);
      }
    }
    this.models.clear();
    this.modelInstances = [];
  }

  /** Set this frame's model instances (drawn inside `draw`). */
  setModelInstances(instances: ModelInstance[]) {
    this.modelInstances = instances;
  }

  /**
   * Set the first-person viewmodel (drawn last with a cleared depth buffer so it
   * never clips into world geometry). Pass null to hide it.
   */
  setViewModel(instance: ModelInstance | null) {
    this.viewModel = instance;
  }

  /** Debug (headless tests): what the renderer will draw as the viewmodel. */
  viewModelDebug(): { model: number; hasModel: boolean; alpha: number; x: number; y: number; z: number } | null {
    const vm = this.viewModel;
    if (!vm) return null;
    return {
      model: vm.model,
      hasModel: this.models.has(vm.model),
      alpha: vm.alpha,
      x: vm.matrix[12],
      y: vm.matrix[13],
      z: vm.matrix[14],
    };
  }

  /** Attach the particle VFX layers (drawn after models, before the viewmodel). */
  setParticles(list: ParticleSystem[]) {
    this.particleSystems = list;
  }

  /** Attach the player-trail ribbons (drawn after models, before particles). */
  setPlayerTrails(trails: PlayerTrailSystem | null) {
    this.playerTrails = trails;
  }

  /**
   * Fog of the Q3 fog volume containing the camera (null when outside). Applied
   * to every world material; the sky becomes the fog colour and fog-volume
   * surfaces are skipped so the shell doesn't double up.
   */
  setSceneFog(fog: FogParms | null) {
    this.sceneFog = fog;
  }

  /** Last frame's CPU submission split (portal/world/models/particles/portals). */
  drawTimes(): Record<string, number> {
    return this.lastDrawTimes;
  }

  /** Portal reflection refresh rate in Hz (0 = every frame). */
  setPortalHz(hz: number) {
    this.portalHz = Math.max(0, hz);
  }

  /** Debug/verification: the frame blend currently set per instance. */
  lastModelFrames() {
    return this.modelInstances.map((i) => [i.frame0, i.frame1, i.lerp]);
  }

  /** Debug/verification: uploaded surface metadata for a model id. */
  modelDebug(id: number) {
    const m = this.models.get(id);
    if (!m) return null;
    return m.surfaces.map((s) => ({
      numVerts: s.numVerts,
      numFrames: s.numFrames,
      indexCount: s.indexCount,
      positionsLen: s.positionsLen,
      frameSums: s.frameSums,
    }));
  }

  /** Draw runtime models. Assumes the world has already filled the depth buffer. */
  private drawModelInstances(projView: Float32Array, eye: [number, number, number]) {
    if (this.wireframe || this.modelInstances.length === 0) return;
    this.drawInstances(projView, this.modelInstances, eye);
  }

  /** Draw a list of model instances with the shared model program. */
  private drawInstances(projView: Float32Array, instances: ModelInstance[], eye?: [number, number, number]) {
    // Runtime models are authored/converted with the opposite winding and are
    // often one-sided cards; draw them two-sided like before.
    this.gl.disable(this.gl.CULL_FACE);
    if (instances.length === 0) return;
    const gl = this.gl;
    this.ensureModelProgram();
    this.useProgram(this.modelProgram);
    gl.uniformMatrix4fv(this.modelProjView, false, projView);
    // Scene fog applies to world models, not the first-person viewmodel (the
    // viewmodel is drawn with a fresh depth buffer and should stay crisp).
    if (eye && this.sceneFog) {
      this.u1(this.modelFogOn, 1);
      this.u3(this.modelEye, eye[0], eye[1], eye[2]);
      this.u3(this.modelFogColor, this.sceneFog.r, this.sceneFog.g, this.sceneFog.b);
      this.u1(this.modelFogDist, this.sceneFog.dist > 0 ? this.sceneFog.dist : 300);
    } else {
      this.u1(this.modelFogOn, 0);
    }
    // Dedicated VAO so model attribute pointers never touch the world VAOs.
    gl.bindVertexArray(this.modelVao);
    for (const inst of instances) {
      const m = this.models.get(inst.model);
      if (!m) continue;
      // Per-instance culling (outline hulls cull front faces so only the
      // silhouette shows); normal runtime models stay two-sided.
      if (inst.cull === "front" || inst.cull === "back") {
        gl.enable(gl.CULL_FACE);
        gl.cullFace(inst.cull === "front" ? gl.FRONT : gl.BACK);
      } else {
        gl.disable(gl.CULL_FACE);
      }
      // Additive glow instances (neon outline shells) write depth so the body
      // drawn after them covers the interior; translucent bodies don't.
      if (inst.glow) {
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
        this.setDepthMask(true);
      } else if (inst.alpha < 1) {
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        this.setDepthMask(false);
      } else {
        gl.disable(gl.BLEND);
        this.setDepthMask(true);
      }
      gl.uniformMatrix4fv(this.modelMatrixLoc, false, inst.matrix);
      this.u3(this.modelColor, inst.color[0], inst.color[1], inst.color[2]);
      this.u1(this.modelAlpha, inst.alpha);
      this.u1(this.modelLerp, inst.lerp);
      for (const s of m.surfaces) {
        const sf0 = Math.min(inst.frame0, s.numFrames - 1);
        const sf1 = Math.min(inst.frame1, s.numFrames - 1);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, s.tex ?? this.whiteTex);
        this.u1(this.modelHasTex, s.tex ? 1 : 0);
        gl.bindBuffer(gl.ARRAY_BUFFER, s.posVbo);
        gl.enableVertexAttribArray(0);
        gl.vertexAttribPointer(0, 3, gl.FLOAT, false, 12, sf0 * s.numVerts * 12);
        gl.enableVertexAttribArray(1);
        gl.vertexAttribPointer(1, 3, gl.FLOAT, false, 12, sf1 * s.numVerts * 12);
        gl.bindBuffer(gl.ARRAY_BUFFER, s.stVbo);
        gl.enableVertexAttribArray(2);
        gl.vertexAttribPointer(2, 2, gl.FLOAT, false, 8, 0);
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, s.ibo);
        gl.drawElements(gl.TRIANGLES, s.indexCount, gl.UNSIGNED_INT, 0);
      }
    }
    this.setDepthMask(true);
    gl.disable(gl.BLEND);
    gl.disable(gl.CULL_FACE);
    gl.bindVertexArray(null);
  }

  private drawSkybox(projView: Float32Array, eye: [number, number, number]) {
    // The skybox is a generated cube with its own winding; keep it two-sided.
    this.gl.disable(this.gl.CULL_FACE);
    if (!this.skyProgram || this.skyRanges.length === 0) return;
    const gl = this.gl;
    this.useProgram(this.skyProgram);
    gl.uniformMatrix4fv(this.skyProjViewLoc, false, projView);
    gl.uniform3fv(this.skyEyeLoc, eye);
    gl.uniform1i(this.skyTexLoc, 2);
    this.u1(this.skyScaleLoc, this.skyScale);
    gl.disable(gl.BLEND);
    gl.bindVertexArray(this.skyVao);
    for (const r of this.skyRanges) {
      const tex = this.skyFaceTextures[r.axis];
      if (!tex) continue;
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.drawElements(gl.TRIANGLES, r.count, gl.UNSIGNED_INT, r.first * 4);
    }
    gl.bindVertexArray(null);
  }

  /** Register a decoded image as a texture, returning a texture id. */
  registerTexture(
    img: { width: number; height: number; data: Uint8ClampedArray | Uint8Array },
    clamp = false,
  ): number {
    const gl = this.gl;
    const tex = gl.createTexture()!;
    const id = this.texCount++;
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    for (let i = 0; i < 8 && gl.getError() !== gl.NO_ERROR; i++) { /* drain */ }
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, img.width, img.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, img.data);
    // A rejected upload (over MAX_TEXTURE_SIZE / out of memory) leaves an
    // incomplete texture that samples *black*; every surface using it would
    // render black. Drop it so the caller falls back to the grey/white texture.
    const err = gl.getError();
    if (err !== gl.NO_ERROR) {
      this.texFailed++;
      console.warn(`[tex] upload ${img.width}x${img.height} failed (0x${err.toString(16)}); using fallback`);
      gl.deleteTexture(tex);
      return -1;
    }
    gl.generateMipmap(gl.TEXTURE_2D);
    if (clamp) this.clampTextures.add(tex);
    this.configureTex(clamp);
    this.textures.set(id, tex);
    return id;
  }

  /**
   * Delete all per-map world/model textures. Called on map load so switching maps
   * (or cycling "random") doesn't leak GPU memory — the VBOs/IBOs are already
   * reused across loads.
   */
  releaseMapTextures() {
    const gl = this.gl;
    for (const tex of this.textures.values()) gl.deleteTexture(tex);
    this.textures.clear();
    this.clampTextures.clear();
  }

  /** Set the texture filtering mode and re-apply it to every loaded texture. */
  setTextureMode(mode: TextureMode) {
    this.textureMode = mode;
    this.applyTextureMode();
  }

  /** Whether anisotropic filtering is available on this GPU. */
  anisoAvailable(): boolean {
    return this.anisoExt !== null && this.anisoMax > 1;
  }

  /** Re-apply the current texture filter mode to every loaded world/sky texture. */
  private applyTextureMode() {
    const gl = this.gl;
    // Use unit 1 so we don't clobber the lightmap binding on unit 0.
    gl.activeTexture(gl.TEXTURE1);
    for (const tex of this.textures.values()) {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      this.configureTex(this.clampTextures.has(tex));
    }
    gl.bindTexture(gl.TEXTURE_2D, this.whiteTex);
    this.configureTex(false);
    for (const tex of this.skyFaceTextures) {
      if (!tex) continue;
      gl.bindTexture(gl.TEXTURE_2D, tex);
      this.configureTex(false);
    }
  }

  /** Apply the current filter mode + anisotropy to the bound texture. */
  private configureTex(clamp: boolean) {
    const gl = this.gl;
    const mode = this.textureMode;
    const mag = mode === "nearest" ? gl.NEAREST : gl.LINEAR;
    const min =
      mode === "nearest"
        ? gl.NEAREST
        : mode === "bilinear"
          ? gl.LINEAR
          : gl.LINEAR_MIPMAP_LINEAR; // trilinear / anisotropic
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, min);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, mag);
    const wrap = clamp ? gl.CLAMP_TO_EDGE : gl.REPEAT;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, wrap);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, wrap);
    if (this.anisoExt) {
      const level = mode === "anisotropic" ? Math.min(this.anisoLevel, this.anisoMax) : 1;
      gl.texParameterf(gl.TEXTURE_2D, this.anisoExt.TEXTURE_MAX_ANISOTROPY_EXT, level);
    }
  }

  /** Fixed filtering for the lightmap atlas (no mips, no mode/aniso). */
  private configureLightmap() {
    const gl = this.gl;
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  draw(
    projView: Float32Array,
    eye: [number, number, number] = [0, 0, 0],
    time = 0,
    moverMatrices: Float32Array[] = [],
  ) {
    const gl = this.gl;
    const passStart = performance.now();
    this.uniCache1.clear();
    this.uniCache2.clear();
    this.uniCache3.clear();
    this.invalidateGlState();
    // `usable()` also covers a failed FBO completeness check (0-sized canvas /
    // driver limits) so a broken bloom target can never black out the scene.
    const bloomOn = this.bloom?.usable() ?? false;
    // The panini warp is a screen-space composite; the first-person viewmodel
    // is drawn after it so the warp can never crop/distort the gun.
    const paniniOn = this.bloom?.panini ?? false;
    const trace = (globalThis as { __glTrace?: boolean }).__glTrace === true;
    if (trace) this.glTrace.length = 0;
    // The scene may render offscreen (bloom) or straight to the screen.
    gl.enable(gl.DEPTH_TEST);
    this.setDepthFunc(gl.LEQUAL);
    this.setDepthMask(true);
    gl.disable(gl.POLYGON_OFFSET_FILL);
    this.useProgram(this.program);
    this.u1(this.uPortalScreen, 0);
    if (trace) gl.getError(); // clear any stale error before tracing
    if (bloomOn) this.bloom!.beginScene();
    this.trace(trace, "beginScene");
    // Wireframe (debug): a near-black background so the green lines read clearly
    // — the normal light-blue clear made them hard to see.
    if (this.wireframe) gl.clearColor(0.04, 0.05, 0.07, 1);
    else if (this.sceneFog) gl.clearColor(this.sceneFog.r, this.sceneFog.g, this.sceneFog.b, 1);
    else gl.clearColor(0.42, 0.58, 0.80, 1);
    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    // Skybox first: it writes depth far away, so the world draws over it. Skip
    // it in wireframe so the lines stand out against the dark clear color, and
    // when inside fog (Q3 fogs the sky to the fog colour).
    if (!this.wireframe && !this.sceneFog) this.drawSkybox(projView, eye);
    this.trace(trace, "skybox");
    const wire = this.wireframe;
    const haveWorld = !(this.indexCount === 0 && this.moverIndexCount === 0);

    if (haveWorld) {
      const tPortalStart = performance.now();
      // Portals: render each surface's reflected view into its own FBO first.
      // Reflections are expensive (a full second world pass); reuse the last
      // FBO between updates, refresh at `portalHz`, and don't update at all when
      // the camera is stationary (a still reflection is still correct).
      let cameraMoved = this.lastPortalProj === null;
      if (!cameraMoved && this.lastPortalProj) {
        for (let i = 0; i < 16; i++) {
          if (Math.abs(projView[i] - this.lastPortalProj[i]) > 1e-5) {
            cameraMoved = true;
            break;
          }
        }
        if (!cameraMoved) {
          for (let i = 0; i < 3; i++) {
            if (Math.abs(eye[i] - this.lastPortalEye[i]) > 0.25) {
              cameraMoved = true;
              break;
            }
          }
        }
      }
      const refreshHz = this.portalHz <= 0 ? 0 : cameraMoved ? this.portalHz : Math.min(this.portalHz, 5);
      const portalDue =
        this.lastPortalUpdateT < 0 || refreshHz <= 0 || time - this.lastPortalUpdateT >= 1 / refreshHz;
      const portalsUpdated = !wire && this.portalChunks.length > 0 && portalDue;
      if (portalsUpdated) {
        this.renderPortalViews(projView, eye, time, moverMatrices);
        this.lastPortalUpdateT = time;
        this.lastPortalProj = new Float32Array(projView);
        this.lastPortalEye = [eye[0], eye[1], eye[2]];
        // Portal views rebound the default FB + full viewport; restore the scene.
        if (bloomOn) this.bloom!.beginScene();
      }
      const tPortalEnd = performance.now();

      // Main pass (portals excluded — they are drawn separately afterwards).
      this.drawWorldPass(projView, eye, time, moverMatrices, wire);
      this.trace(trace, "world");
      const tWorldEnd = performance.now();

      // Runtime models (MD3), depth-tested against the world.
      this.drawModelInstances(projView, eye);
      this.trace(trace, "models");
      const tModelsEnd = performance.now();

      // Client-local particle VFX (depth-tested against the world), in layer
      // order (e.g. alpha smoke first, additive glow on top).
      if (!wire) {
        // Billboards + trails: always two-sided.
        gl.disable(gl.CULL_FACE);
        this.playerTrails?.draw(projView, eye);
        // The trail system uses its own program/state directly; forget the
        // cached state before the particle passes touch the same GL objects.
        this.invalidateGlState();
        for (const ps of this.particleSystems) ps.draw(projView, eye);
        // The particle systems use their own programs/state directly; forget
        // the cached values so the viewmodel pass rebinds what it needs.
        this.invalidateGlState();
      }
      this.trace(trace, "particles");
      const tParticlesEnd = performance.now();

      // First-person viewmodel: drawn last with a fresh depth buffer so it can
      // never clip into (or be hidden by) world geometry. When panini is on it
      // is drawn after the composite instead (see below).
      if (!wire && this.viewModel && !paniniOn) {
        gl.clear(gl.DEPTH_BUFFER_BIT);
        this.drawInstances(projView, [this.viewModel]);
      }
      const tViewModelEnd = performance.now();

      if (!wire && this.portalChunks.length > 0) {
        this.drawPortalSurfaces(time);
      }
      const tPortalSurfEnd = performance.now();
      this.lastDrawTimes = {
        portalViews: +(tPortalEnd - tPortalStart).toFixed(3),
        world: +(tWorldEnd - tPortalEnd).toFixed(3),
        worldRuns: this.lastWorldRuns,
        worldGroups: this.lastWorldGroups,
        models: +(tModelsEnd - tWorldEnd).toFixed(3),
        particles: +(tParticlesEnd - tModelsEnd).toFixed(3),
        viewmodel: +(tViewModelEnd - tParticlesEnd).toFixed(3),
        portalSurfaces: +(tPortalSurfEnd - tViewModelEnd).toFixed(3),
        total: +(tPortalSurfEnd - passStart).toFixed(3),
      };
    }

    gl.disable(gl.BLEND);
    gl.disable(gl.CULL_FACE);
    gl.bindVertexArray(null);
    if (bloomOn) this.bloom!.apply();
    this.trace(trace, "apply");
    // Panini: the composite has already warped the world. Draw the viewmodel
    // straight to the canvas (rectilinear, fresh depth) so it stays unwarped.
    if (!wire && this.viewModel && paniniOn) {
      const t0 = performance.now();
      this.invalidateGlState();
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
      gl.clear(gl.DEPTH_BUFFER_BIT);
      this.drawInstances(projView, [this.viewModel]);
      this.lastDrawTimes.viewmodel = +(performance.now() - t0).toFixed(3);
    }
  }

  /** Debug: recent per-stage glGetError trace (enable with `__glTrace`). */
  private glTrace: string[] = [];
  glTraceDump(): string[] {
    return this.glTrace;
  }
  private trace(on: boolean, label: string) {
    if (!on) return;
    this.glTrace.push(`${label}=${this.gl.getError()}`);
  }

  /** Draw the static world + movers with the given (possibly reflected) projView. */
  private drawWorldPass(
    projView: Float32Array,
    eye: [number, number, number],
    time: number,
    moverMatrices: Float32Array[],
    wire: boolean,
    mirrored = false,
  ) {
    const gl = this.gl;
    const primitive = wire ? gl.LINES : gl.TRIANGLES;
    // Frame-global uniforms; they persist per-program across chunk switches.
    this.useProgram(this.program);
    gl.uniformMatrix4fv(this.uProjView, false, projView);
    this.u1(this.uWire, wire ? 1 : 0);
    this.u3(this.uEye, eye[0], eye[1], eye[2]);
    this.u1(this.uTime, time);
    this.useProgram(this.fxProgram);
    gl.uniformMatrix4fv(this.fxProjView, false, projView);
    gl.uniformMatrix4fv(this.fxModel, false, IDENTITY);

    // PVS uses either multi-draws straight from the static IBO (preferred) or
    // a dynamic index buffer grouped by material; wireframe keeps the static
    // line-index path (debug only).
    const dynamicPvs =
      !wire && this.visibleRuns !== null && (this.multiDraw !== null || this.visIbo !== null);
    if (dynamicPvs) this.rebuildVisibleGroups();
    const multiPvs = dynamicPvs && this.multiDraw !== null;
    const passes = wire ? [true] : [true, false];
    for (const opaquePass of passes) {
      gl.bindVertexArray(dynamicPvs ? (multiPvs ? this.vao : this.visVao!) : wire ? this.wireVao : this.vao);
      if (this.visibleRuns) {
        // PVS path: draw only the visible index ranges (0 runs = draw nothing,
        // e.g. every PVS face was frustum-culled). `materials` is indexed
        // by BSP shader id; null entries (portal/no-draw) are skipped.
        this.useProgram(this.program);
        gl.uniformMatrix4fv(this.uModel, false, IDENTITY);
        if (dynamicPvs) {
          let groups = 0;
          for (const g of this.visGroups) {
            if ((g.mat.blend === "opaque") !== opaquePass) continue;
            this.drawMaterialBody(
              g.mat,
              g.start,
              g.count,
              time,
              primitive,
              false,
              IDENTITY,
              mirrored,
              multiPvs ? { start: g.start, runs: g.count } : undefined,
            );
            groups++;
          }
          if (opaquePass) {
            this.lastWorldRuns = this.visibleRunCount;
            this.lastWorldGroups = groups;
          }
        } else {
          const runs = this.visibleRuns;
          const n = this.visibleRunCount;
          let r = 0;
          let groups = 0;
          while (r < n) {
            const mat = this.materials[runs[r * 3]];
            if (!mat || (mat.blend === "opaque") !== opaquePass) {
              r++;
              continue;
            }
            // Runs are emitted in face order, and faces are grouped by shader,
            // so all runs of one material are adjacent.
            let e = r + 1;
            while (e < n && this.materials[runs[e * 3]] === mat) e++;
            this.drawMaterialRuns(mat, runs, r, e, time, primitive, mirrored);
            groups++;
            r = e;
          }
          if (opaquePass) {
            this.lastWorldRuns = n;
            this.lastWorldGroups = groups;
          }
        }
      } else if (this.chunks.length === 0) {
        if (!opaquePass) continue;
        this.useProgram(this.program);
        gl.uniformMatrix4fv(this.uModel, false, IDENTITY);
        this.u1(this.uHasTexture, 0);
        this.u1(this.uLit, 1);
        this.u2(this.uUvX, 1, 0);
        this.u2(this.uUvY, 0, 1);
        this.u2(this.uUvOff, 0, 0);
        this.u3(this.uRgb, 1, 1, 1);
        this.u1(this.uRgbVertex, 0);
        this.u1(this.uAlpha, 1);
        this.u1(this.uAlphaFunc, 0);
        this.u1(this.uFogOn, this.sceneFog ? 1 : 0);
        if (this.sceneFog) {
          this.u3(this.uFogColor, this.sceneFog.r, this.sceneFog.g, this.sceneFog.b);
          this.u1(this.uFogDist, this.sceneFog.dist > 0 ? this.sceneFog.dist : 300);
        }
        this.u1(this.uFogSurface, 0);
        this.u1(this.uPortalRgb, 0);
        this.u1(this.uPortalAlpha, 0);
        this.u1(this.uTcGen, 0);
        this.u1(this.uDeformMode, 0);
        this.setCull("front", mirrored);
        this.setBlend("opaque");
        gl.drawElements(
          primitive,
          wire ? this.wireIndexCount : this.indexCount,
          gl.UNSIGNED_INT,
          0,
        );
        // No chunks: one draw over the whole static index buffer (draw-all
        // fallback). `-1` tells `diag` this was not a PVS run list.
        this.lastWorldRuns = -1;
        this.lastWorldGroups = 1;
      } else {
        this.useProgram(this.program);
        gl.uniformMatrix4fv(this.uModel, false, IDENTITY);
        let groups = 0;
        for (const c of this.chunks) {
          if ((c.blend === "opaque") !== opaquePass) continue;
          this.drawChunkBody(c, time, primitive, false, IDENTITY, mirrored);
          groups++;
        }
        if (opaquePass) {
          this.lastWorldRuns = -1; // draw-all fallback, not a PVS run list
          this.lastWorldGroups = groups;
        }
      }

      if (this.moverVao && this.moverIndexCount > 0 && this.moverChunks.length > 0) {
        gl.bindVertexArray(wire ? this.moverWireVao : this.moverVao);
        for (const c of this.moverChunks) {
          if ((c.blend === "opaque") !== opaquePass) continue;
          const model = moverMatrices[c.model] ?? IDENTITY;
          this.drawChunkBody(c, time, primitive, true, model, mirrored);
        }
      }
    }
  }

  /** Render each portal surface's reflected view into its own framebuffer. */
  private renderPortalViews(
    projView: Float32Array,
    eye: [number, number, number],
    time: number,
    moverMatrices: Float32Array[],
  ) {
    const gl = this.gl;
    const w = this.canvasW;
    const h = this.canvasH;
    // Reflected views use the main camera's PVS as a conservative visible set.
    // It is an approximation (the reflected eye can see a little more), but it
    // cuts a portal update from the full world (135k tris) to the main view's
    // ~22k, which matters because portals update at 30 Hz on top of the scene.
    const savedRuns = this.visibleRuns;
    const savedCount = this.visibleRunCount;
    for (const p of this.portalChunks) {
      const fbo = this.ensurePortalFbo(p, w, h);
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo.fb);
      gl.viewport(0, 0, fbo.w, fbo.h);
      gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
      // Reflect the view across the portal plane: projView * R.
      const reflected = multiply(projView, reflectMatrix(p.plane.n, p.plane.p));
      // Draw the sky first from the mirrored eye, otherwise the mirror shows
      // the FBO's clear colour (light blue) wherever the sky should be.
      const n = p.plane.n;
      const pp = p.plane.p;
      const d = n[0] * pp[0] + n[1] * pp[1] + n[2] * pp[2];
      const dist = n[0] * eye[0] + n[1] * eye[1] + n[2] * eye[2] - d;
      const eyeRef: [number, number, number] = [
        eye[0] - 2 * dist * n[0],
        eye[1] - 2 * dist * n[1],
        eye[2] - 2 * dist * n[2],
      ];
      this.drawSkybox(reflected, eyeRef);
      this.drawWorldPass(reflected, eye, time, moverMatrices, false, true);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, w, h);
    this.visibleRuns = savedRuns;
    this.visibleRunCount = savedCount;
  }

  private ensurePortalFbo(p: PortalChunk, w: number, h: number): PortalFbo {
    const gl = this.gl;
    // Reflective surfaces don't need full resolution, and full-canvas FBOs per
    // portal are a lot of VRAM at 1440p+ (and churn on every resize). Cap the
    // long edge.
    const scale = Math.min(1, 1280 / Math.max(1, w, h));
    const pw = Math.max(1, Math.round(w * scale));
    const ph = Math.max(1, Math.round(h * scale));
    let f = this.portalFbos.get(p);
    if (f && f.w === pw && f.h === ph) return f;
    if (f) {
      gl.deleteFramebuffer(f.fb);
      gl.deleteTexture(f.color);
      gl.deleteRenderbuffer(f.depth);
    }
    const color = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, color);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, pw, ph, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    const depth = gl.createRenderbuffer()!;
    gl.bindRenderbuffer(gl.RENDERBUFFER, depth);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, pw, ph);
    const fb = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, color, 0);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depth);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    f = { fb, color, depth, w: pw, h: ph };
    this.portalFbos.set(p, f);
    return f;
  }

  /** Draw portal surfaces with their reflected-view texture. */
  private drawPortalSurfaces(time: number) {
    const gl = this.gl;
    gl.bindVertexArray(this.vao);
    this.u1(this.uWire, 0);
    for (const p of this.portalChunks) {
      const fbo = this.portalFbos.get(p);
      if (!fbo) continue;
      this.setCull(p.cull);
      this.useProgram(this.program);
      if (p.polygonOffset) {
        gl.enable(gl.POLYGON_OFFSET_FILL);
        gl.polygonOffset(-1, -1);
      } else {
        gl.disable(gl.POLYGON_OFFSET_FILL);
      }
      // The reflected FBO was rendered with the mirrored camera; sample it at
      // the fragment's screen position (a mirror, not the surface's UVs).
      this.u1(this.uPortalScreen, 1);
      this.u2(this.uScreenSize, this.canvasW, this.canvasH);
      gl.uniformMatrix4fv(this.uModel, false, IDENTITY);
      this.u1(this.uLit, 0);
      this.u2(this.uUvX, 1, 0);
      this.u2(this.uUvY, 0, 1);
      this.u2(this.uUvOff, 0, 0);
      this.u3(this.uRgb, 1, 1, 1);
      this.u1(this.uRgbVertex, 0);
      this.u1(this.uAlpha, 1);
      this.u1(this.uAlphaFunc, 0);
      this.u1(this.uFogOn, this.sceneFog ? 1 : 0);
      if (this.sceneFog) {
        this.u3(this.uFogColor, this.sceneFog.r, this.sceneFog.g, this.sceneFog.b);
        this.u1(this.uFogDist, this.sceneFog.dist > 0 ? this.sceneFog.dist : 300);
      }
      this.u1(this.uFogSurface, 0);
      this.u1(this.uPortalRgb, 0);
      this.u1(this.uPortalAlpha, 0);
      this.u1(this.uTcGen, 0);
      this.u1(this.uDeformMode, 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, fbo.color);
      this.u1(this.uHasTexture, 1);
      this.setBlend("blend");
      gl.drawElements(gl.TRIANGLES, p.count, gl.UNSIGNED_INT, p.first * 4);
      // The portal shader's own texture is the tint/mask (often semi-transparent
      // stone/glass): draw it over the reflection. Ignoring it leaves a pure
      // mirror, which reads as flat sky blue on 13black_hd's floors.
      const baseId = this.pickAnim(p.tex, p.animFreq, time);
      const baseTex = baseId !== null ? this.textures.get(baseId) : undefined;
      if (baseTex) {
        this.u1(this.uPortalScreen, 0);
        this.u1(this.uHasTexture, 1);
        this.u1(this.uLit, 0);
        this.u1(this.uTcGen, 0);
        this.u1(this.uDeformMode, 0);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, baseTex);
        this.setBlend(p.blend === "opaque" ? "blend" : p.blend);
        gl.drawElements(gl.TRIANGLES, p.count, gl.UNSIGNED_INT, p.first * 4);
        this.u1(this.uPortalScreen, 0);
      }
      // Overlay stages (e.g. a TV-scanline overlay) on top.
      for (const o of p.overlays) {
        const otex = this.textures.get(o.tex);
        if (!otex) continue;
        this.useProgram(this.fxProgram);
        this.setFxEffects(o.effects, time);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, otex);
        this.setBlend(o.blend);
        gl.drawElements(gl.TRIANGLES, p.count, gl.UNSIGNED_INT, p.first * 4);
      }
    }
    gl.disable(gl.POLYGON_OFFSET_FILL);
    this.useProgram(this.program);
    this.u1(this.uPortalScreen, 0);
  }

  /**
   * Draw one chunk's base pass plus overlay passes. Frame-global uniforms
   * (`uProjView`/`uTime`/`uWire`, and `fx*` equivalents) are set by `draw`;
   * this only sets what differs per chunk (texture, lit, scroll, blend) plus
   * the model matrix for movers (`setModel`).
   */
  private drawChunkBody(
    c: DrawChunk,
    time: number,
    primitive: number,
    setModel: boolean,
    model: Float32Array = IDENTITY,
    mirrored = false,
  ) {
    this.drawMaterialBody(c, c.first, c.count, time, primitive, setModel, model, mirrored);
  }

  /**
   * Draw a material's base + overlay passes over several index ranges (PVS
   * runs). Material state is bound once per group instead of once per face-run;
   * this is the hot path for big open maps (13octo: 366 visible runs).
   */
  private drawMaterialRuns(
    m: DrawMaterial,
    runs: Uint32Array,
    r0: number,
    r1: number,
    time: number,
    primitive: number,
    mirrored: boolean,
  ) {
    const gl = this.gl;
    const wire = this.wireframe;
    if (wire === false && m.fogVolume && this.sceneFog) return;
    this.setCull(m.cull, mirrored);
    if (m.polygonOffset) {
      gl.enable(gl.POLYGON_OFFSET_FILL);
      gl.polygonOffset(-1, -1);
    } else {
      gl.disable(gl.POLYGON_OFFSET_FILL);
    }
    this.setDepthMask(m.blend === "opaque" && !wire);
    this.setDepthFunc(!wire && m.effects.depthFuncEqual ? gl.EQUAL : gl.LEQUAL);
    this.useProgram(this.program);
    const baseId = this.pickAnim(m.tex, m.animFreq, time);
    const baseTex = baseId !== null ? this.textures.get(baseId) : undefined;
    if (baseTex) {
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, baseTex);
      this.u1(this.uHasTexture, 1);
    } else {
      this.u1(this.uHasTexture, 0);
    }
    this.u1(this.uLit, m.lit ? 1 : 0);
    this.setBaseEffects(m.effects, this.sceneFog ?? m.fog, time);
    this.u1(this.uFogSurface, m.fogVolume ? 1 : 0);
    this.setBlend(wire ? "opaque" : m.blend);
    for (let i = r0; i < r1; i++) {
      const first = runs[i * 3 + 1];
      const count = runs[i * 3 + 2];
      gl.drawElements(
        primitive,
        wire ? count * 2 : count,
        gl.UNSIGNED_INT,
        (wire ? first * 2 : first) * 4,
      );
    }
    if (wire) {
      this.setDepthMask(true);
      return;
    }
    for (const o of m.overlays) {
      const otex = this.textures.get(o.tex);
      if (!otex) continue;
      this.useProgram(this.fxProgram);
      this.setFxEffects(o.effects, time);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, otex);
      this.setBlend(o.blend);
      this.setDepthFunc(o.effects.depthFuncEqual ? gl.EQUAL : gl.LEQUAL);
      this.setDepthMask(o.blend === "opaque");
      for (let i = r0; i < r1; i++) {
        gl.drawElements(
          gl.TRIANGLES,
          runs[i * 3 + 2],
          gl.UNSIGNED_INT,
          runs[i * 3 + 1] * 4,
        );
      }
    }
    this.setDepthFunc(gl.LEQUAL);
    this.setDepthMask(true);
    gl.disable(gl.POLYGON_OFFSET_FILL);
  }

  /**
   * Draw one material's base pass plus overlay passes over an index range.
   * Frame-global uniforms (`uProjView`/`uTime`/`uWire`, and `fx*` equivalents)
   * are set by `draw`; this only sets what differs per range (texture, lit,
   * scroll, blend) plus the model matrix for movers.
   */
  private drawMaterialBody(
    m: DrawMaterial,
    firstIndex: number,
    indexCount: number,
    time: number,
    primitive: number,
    setModel: boolean,
    model: Float32Array = IDENTITY,
    mirrored = false,
    multi?: { start: number; runs: number },
  ) {
    const gl = this.gl;
    const wire = this.wireframe;
    // Inside a fog volume the shell itself must not be drawn (the scene fog
    // already covers the view); otherwise it would stack a coloured wall.
    if (wire === false && m.fogVolume && this.sceneFog) return;
    this.setCull(m.cull, mirrored);
    if (m.polygonOffset) {
      gl.enable(gl.POLYGON_OFFSET_FILL);
      gl.polygonOffset(-1, -1);
    } else {
      gl.disable(gl.POLYGON_OFFSET_FILL);
    }
    // Translucent/additive passes must not write depth: a beam/glass surface
    // drawn before an opaque wall would otherwise reject everything behind it
    // (looked like see-through holes when jumping into a light beam). Opaque
    // materials restore depth writes for the next chunk.
    this.setDepthMask(m.blend === "opaque" && !wire);
    this.setDepthFunc(!wire && m.effects.depthFuncEqual ? gl.EQUAL : gl.LEQUAL);
    // Base pass.
    this.useProgram(this.program);
    if (setModel) gl.uniformMatrix4fv(this.uModel, false, model);
    const baseId = this.pickAnim(m.tex, m.animFreq, time);
    const baseTex = baseId !== null ? this.textures.get(baseId) : undefined;
    if (baseTex) {
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, baseTex);
      this.u1(this.uHasTexture, 1);
    } else {
      this.u1(this.uHasTexture, 0);
    }
    this.u1(this.uLit, m.lit ? 1 : 0);
    this.setBaseEffects(m.effects, this.sceneFog ?? m.fog, time);
    this.u1(this.uFogSurface, m.fogVolume ? 1 : 0);
    this.setBlend(wire ? "opaque" : m.blend);
    // In wireframe the bound IBO is the line buffer, whose range is 2x the
    // triangle range in the same order.
    const first = wire ? firstIndex * 2 : firstIndex;
    const count = wire ? indexCount * 2 : indexCount;
    this.drawRange(primitive, first, count, wire ? undefined : multi);

    // Overlay passes (additive / blended / multiply). Skipped in wireframe.
    if (wire) {
      this.setDepthMask(true);
      return;
    }
    for (const o of m.overlays) {
      const otex = this.textures.get(o.tex);
      if (!otex) continue;
      this.useProgram(this.fxProgram);
      if (setModel) gl.uniformMatrix4fv(this.fxModel, false, model);
      this.setFxEffects(o.effects, time);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, otex);
      this.setBlend(o.blend);
      this.setDepthFunc(o.effects.depthFuncEqual ? gl.EQUAL : gl.LEQUAL);
      // Blended passes never write depth (Q3 draws them after the opaque pass).
      this.setDepthMask(o.blend === "opaque");
      this.drawRange(gl.TRIANGLES, firstIndex, indexCount, multi);
    }
    this.setDepthFunc(gl.LEQUAL);
    this.setDepthMask(true);
    gl.disable(gl.POLYGON_OFFSET_FILL);
  }

  /**
   * One draw for a material group. With `WEBGL_multi_draw` the group's runs are
   * drawn straight from the static map IBO (`visCounts`/`visOffsets`), so the
   * per-frame index copy + upload disappear; otherwise a plain range draw from
   * `visIbo` is used.
   */
  private drawRange(
    primitive: number,
    firstIndex: number,
    indexCount: number,
    multi?: { start: number; runs: number },
  ) {
    if (multi && this.multiDraw) {
      this.multiDraw.multiDrawElementsWEBGL(
        primitive,
        this.visCounts,
        multi.start,
        this.gl.UNSIGNED_INT,
        this.visOffsets,
        multi.start,
        multi.runs,
      );
      return;
    }
    this.gl.drawElements(primitive, indexCount, this.gl.UNSIGNED_INT, firstIndex * 4);
  }

  private setBaseEffects(e: StageEffects, fog: FogParms | undefined, time: number) {
    const gl = this.gl;
    const uv = computeUv(e.tcMods, time);
    this.u2(this.uUvX, uv[0], uv[1]);
    this.u2(this.uUvY, uv[2], uv[3]);
    this.u2(this.uUvOff, uv[4], uv[5]);
    const rgb = computeRgb(e.rgb, time);
    this.u3(this.uRgb, rgb.r, rgb.g, rgb.b);
    this.u1(this.uRgbVertex, rgb.vertex ? 1 : 0);
    const al = computeAlpha(e.alpha, time);
    this.u1(this.uAlpha, al.value);
    this.u1(
      this.uAlphaFunc,
      e.alphaFunc === "gt0" ? 1 : e.alphaFunc === "lt128" ? 2 : e.alphaFunc === "ge128" ? 3 : 0,
    );
    this.u1(this.uPortalRgb, rgb.portal ? 1 : 0);
    this.u1(this.uPortalAlpha, al.portal ? 1 : 0);
    this.u1(this.uFogOn, fog ? 1 : 0);
    if (fog) {
      this.u3(this.uFogColor, fog.r, fog.g, fog.b);
      this.u1(this.uFogDist, fog.dist > 0 ? fog.dist : 300);
    }

    // tcGen (0 affine, 1 environment, 2 lightmap, 3 vector).
    const vectorGen = e.tcGen === "vector" || e.tcGen === "ivector";
    this.u1(
      this.uTcGen,
      vectorGen ? 3 : e.tcGen === "environment" ? 1 : e.tcGen === "lightmap" ? 2 : 0,
    );
    const s = e.tcGenS ?? [0, 0, 0];
    const tt = e.tcGenT ?? [0, 0, 0];
    this.u3(this.uTcGenS, s[0], s[1], s[2]);
    this.u3(this.uTcGenT, tt[0], tt[1], tt[2]);

    // deformVertexes.
    const d = e.deform;
    if (d && d.kind === "wave") {
      this.u1(this.uDeformMode, 1);
      gl.uniform4f(this.uDeformParams, d.amp ?? 0, d.base ?? 0, d.freq ?? 0, d.div ?? 1);
      this.u1(this.uDeformPhase, d.phase ?? 0);
      this.u1(this.uDeformFunc, waveFuncId(d.func));
      this.u3(this.uDeformVec, 0, 0, 0);
    } else if (d && d.kind === "move") {
      this.u1(this.uDeformMode, 2);
      const m = d.move ?? [0, 0, 0];
      this.u3(this.uDeformVec, m[0], m[1], m[2]);
      gl.uniform4f(this.uDeformParams, d.amp ?? 0, d.base ?? 0, d.freq ?? 0, 0);
      this.u1(this.uDeformPhase, d.phase ?? 0);
      this.u1(this.uDeformFunc, waveFuncId(d.func));
    } else if (d && d.kind === "normal") {
      this.u1(this.uDeformMode, 3);
      gl.uniform4f(this.uDeformParams, d.amp ?? 0, d.freq ?? 0, 0, 0);
      this.u1(this.uDeformPhase, 0);
      this.u3(this.uDeformVec, 0, 0, 0);
    } else if (d && d.kind === "bulge") {
      // params = (height, width, speed).
      this.u1(this.uDeformMode, 6);
      gl.uniform4f(
        this.uDeformParams,
        d.bulgeHeight ?? 0,
        d.bulgeWidth ?? 1,
        d.bulgeSpeed ?? 0,
        0,
      );
      this.u1(this.uDeformPhase, 0);
      this.u3(this.uDeformVec, 0, 0, 0);
    } else if (d && d.kind === "autosprite") {
      this.u1(this.uDeformMode, 4);
    } else if (d && d.kind === "autosprite2") {
      this.u1(this.uDeformMode, 5);
    } else {
      this.u1(this.uDeformMode, 0);
    }
  }

  private setFxEffects(e: StageEffects, time: number) {
    const uv = computeUv(e.tcMods, time);
    this.u2(this.fxUvX, uv[0], uv[1]);
    this.u2(this.fxUvY, uv[2], uv[3]);
    this.u2(this.fxUvOff, uv[4], uv[5]);
    const rgb = computeRgb(e.rgb, time);
    this.u3(this.fxRgb, rgb.r, rgb.g, rgb.b);
    this.u1(this.fxAlpha, computeAlpha(e.alpha, time).value);
    this.u1(
      this.fxAlphaFunc,
      e.alphaFunc === "gt0" ? 1 : e.alphaFunc === "lt128" ? 2 : e.alphaFunc === "ge128" ? 3 : 0,
    );
  }
}

/** Build a `gl.LINES` index buffer (3 edges per triangle) from triangle indices. */
function buildLineIndices(tris: Uint32Array): Uint32Array {
  const lines = new Uint32Array((tris.length / 3) * 6);
  let o = 0;
  for (let i = 0; i + 2 < tris.length; i += 3) {
    const a = tris[i];
    const b = tris[i + 1];
    const c = tris[i + 2];
    lines[o++] = a;
    lines[o++] = b;
    lines[o++] = b;
    lines[o++] = c;
    lines[o++] = c;
    lines[o++] = a;
  }
  return lines;
}

// 4x4 matrix helpers (column-major floats). Kept minimal and inlined.
export function perspective(fovYRad: number, aspect: number, near: number, far: number): Float32Array {
  const f = 1 / Math.tan(fovYRad / 2);
  const out = new Float32Array(16);
  out[0] = f / aspect; out[5] = f;
  out[10] = (far + near) / (near - far);
  out[11] = -1;
  out[14] = (2 * far * near) / (near - far);
  return out;
}

export function lookAt(eye: [number, number, number], yaw: number, pitch: number): Float32Array {
  // Build a view matrix from eye + euler (yaw around Z-up in our space).
  const cy = Math.cos(yaw), sy = Math.sin(yaw);
  const cp = Math.cos(pitch), sp = Math.sin(pitch);
  // forward
  const fx = cy * cp, fy = sy * cp, fz = sp;
  // right
  const rx = sy, ry = -cy, rz = 0;
  // up = right x forward
  const ux = ry * fz - rz * fy;
  const uy = rz * fx - rx * fz;
  const uz = rx * fy - ry * fx;
  const m = new Float32Array(16);
  m[0] = rx; m[1] = ux; m[2] = -fx; m[3] = 0;
  m[4] = ry; m[5] = uy; m[6] = -fy; m[7] = 0;
  m[8] = rz; m[9] = uz; m[10] = -fz; m[11] = 0;
  m[12] = -(rx * eye[0] + ry * eye[1] + rz * eye[2]);
  m[13] = -(ux * eye[0] + uy * eye[1] + uz * eye[2]);
  m[14] = -(-fx * eye[0] - fy * eye[1] - fz * eye[2]);
  m[15] = 1;
  return m;
}

export function multiply(a: Float32Array, b: Float32Array): Float32Array {
  const o = new Float32Array(16);
  for (let c = 0; c < 4; c++)
    for (let r = 0; r < 4; r++) {
      let s = 0;
      for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k];
      o[c * 4 + r] = s;
    }
  return o;
}

/**
 * Extract the six view-frustum planes from a column-major `projView` matrix
 * (Gribb–Hartmann), writing `[a,b,c,d]` per plane into `out` (24 floats, inside
 * is `a*x+b*y+c*z+d >= 0`). Order: left, right, bottom, top, near, far. Matches
 * the layout used by `perspective`/`lookAt`/`multiply` above.
 */
export function extractFrustum(projView: Float32Array, out: Float32Array): void {
  // Rows of the clip matrix: row_i = (m[i], m[i+4], m[i+8], m[i+12]).
  const r0 = [projView[0], projView[4], projView[8], projView[12]];
  const r1 = [projView[1], projView[5], projView[9], projView[13]];
  const r2 = [projView[2], projView[6], projView[10], projView[14]];
  const r3 = [projView[3], projView[7], projView[11], projView[15]];
  const set = (i: number, a: number, b: number, c: number, d: number) => {
    out[i * 4] = a; out[i * 4 + 1] = b; out[i * 4 + 2] = c; out[i * 4 + 3] = d;
  };
  set(0, r3[0] + r0[0], r3[1] + r0[1], r3[2] + r0[2], r3[3] + r0[3]); // left
  set(1, r3[0] - r0[0], r3[1] - r0[1], r3[2] - r0[2], r3[3] - r0[3]); // right
  set(2, r3[0] + r1[0], r3[1] + r1[1], r3[2] + r1[2], r3[3] + r1[3]); // bottom
  set(3, r3[0] - r1[0], r3[1] - r1[1], r3[2] - r1[2], r3[3] - r1[3]); // top
  set(4, r3[0] + r2[0], r3[1] + r2[1], r3[2] + r2[2], r3[3] + r2[3]); // near
  set(5, r3[0] - r2[0], r3[1] - r2[1], r3[2] - r2[2], r3[3] - r2[3]); // far
}

/**
 * 4x4 reflection matrix across the plane through `p` with normal `n`
 * (column-major), for reflecting the view matrix: `projView * reflect`.
 */
export function reflectMatrix(n: [number, number, number], p: [number, number, number]): Float32Array {
  const [nx, ny, nz] = n;
  const d = nx * p[0] + ny * p[1] + nz * p[2];
  const m = new Float32Array(16);
  m[0] = 1 - 2 * nx * nx; m[4] = -2 * nx * ny; m[8] = -2 * nx * nz; m[12] = 2 * d * nx;
  m[1] = -2 * ny * nx; m[5] = 1 - 2 * ny * ny; m[9] = -2 * ny * nz; m[13] = 2 * d * ny;
  m[2] = -2 * nz * nx; m[6] = -2 * nz * ny; m[10] = 1 - 2 * nz * nz; m[14] = 2 * d * nz;
  m[3] = 0; m[7] = 0; m[11] = 0; m[15] = 1;
  return m;
}
